/**
 * NodeDetailModal - Modal for viewing and editing a metric node's details
 *
 * Group Cards can edit:
 * - Name
 * - Aggregation type (min vs average)
 *
 * Data Cards can edit:
 * - Name
 * - Data value type (number vs percentage)
 * - Data values for each period
 * - Level boundaries (assurance rules)
 */
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePostHog } from 'src/lib/posthog';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'src/components/ui/toast';
import {
  CheckIcon,
  CopyIcon,
  FloppyDiskIcon,
  GearIcon,
  PlusIcon,
  ArrowSquareOutIcon,
  LinkIcon,
  TrashIcon,
  UploadSimpleIcon,
  WarningIcon,
  ChartLineIcon,
  ChartBarIcon,
  GaugeIcon,
  type Icon
} from '@phosphor-icons/react';
import { apiGet } from '@/lib/api/axios';
import { formatRelativeTime } from '@/lib/utils/formatRelativeTime';
import { ConfirmModal } from 'src/components/ui/ConfirmModal';
import Button from 'src/components/ui/Button';
import { InputField } from 'src/components/ui/Input';
import SegmentedControl from 'src/components/ui/SegmentedControl';
import Banner from 'src/components/ui/Banner';
import Dropdown from 'src/components/ui/Dropdown';
import Tabs from 'src/components/ui/Tabs';
import Loading from 'src/components/ui/Loading';
import DataTable from 'src/components/ui/Table';
import DataValuesEditableTable from './DataValuesEditableTable';
import ThresholdBandBar from './ThresholdBandBar';
import CalculationModePicker from './CalculationModePicker';
import RuleStep from './RuleStep';
import AssuranceRuleSummary from './AssuranceRuleSummary';
import ImportDataModal from './ImportDataModal';
import CardRemindersTab from './CardRemindersTab';
import RemindersTab from './RemindersTab';
import type { ReminderEditor } from './reminderEditor';
import { TimelineShell } from 'src/features/pam/PamActivityTimeline';
import {
  useMetricNode,
  useUpdateMetricNode,
  useDeleteMetricNode,
  useDuplicateCard,
  useDataValues,
  useUpsertDataValue,
  useDeleteDataValue,
  useLevelBoundaries,
  useUpsertLevelBoundaries,
  useBaseValues,
  useUpsertBaseValue,
  useDeleteBaseValue,
  useLinkedDashboards,
  useAvailableParents,
  useNodeAuditLogs,
  useNodePermissions,
  useShareNode
} from '../api/hooks';
import type {
  Level,
  LevelBoundaryInput,
  AggregationType,
  DataValueType,
  BoundaryDirection,
  LevelCalculationMode,
  BaseValueType,
  UpdateCadence,
  ImportPreviewResponse,
  NodeAuditLogEntry,
  DataValueUpsert,
  GroupNumericDisplayType,
  ChildScoreAggregation,
  MetricNodeType,
  CardChartType
} from '../api/types';
import { MONTHS, formatPeriodLabel } from '../utils/constants';
import {
  validateLevelBoundaries,
  parseThresholdInput,
  getThresholdOrderDescription,
  getQuarterFromMonth,
  QUARTER_END_MONTHS,
  type ValidationError
} from '../utils/validation';
import Modal from 'src/components/ui/Modal';
import { Toggle } from 'src/components/ui/Toggle';
import useAuth from 'src/hooks/useAuth';
import { useMentionShareFlow } from 'src/hooks/useMentionShareFlow';
import ShareModal from 'src/components/ui/ShareModal';
import { ResourcePermissionRole } from 'src/features/chat/types';
import { useChartTilesEnabled } from '../hooks/useDashboardTileStyle';
import { chartTypesFor, resolveChartType } from '../utils/chartType';
import { snakeToTitleCase } from 'src/lib/utils/formatLabel';

const CHART_TYPE_OPTIONS: Record<CardChartType, { value: CardChartType; label: string; icon: Icon }> = {
  line: { value: 'line', label: 'Line', icon: ChartLineIcon },
  bar: { value: 'bar', label: 'Bar', icon: ChartBarIcon },
  dial: { value: 'dial', label: 'Dial', icon: GaugeIcon }
};

interface NodeDetailModalProps {
  nodeId: string;
  scopeId: string;
  isOpen: boolean;
  onClose: () => void;
  onDeleted?: () => void;
  readOnly?: boolean;
  /** When true, only data values can be edited — name, rules, move, delete, duplicate are disabled */
  dataEntryOnly?: boolean;
  /** For group cards: direct children available as pinned-value candidates */
  groupChildren?: Array<{ id: string; title: string; type: MetricNodeType; groupNumericDisplayValue?: number | null }>;
  /** `none` renders the form without the modal shell (card details pane). */
  chrome?: 'modal' | 'none';
  /** Which tabs to show. Defaults to the full edit modal. */
  sections?: 'full' | 'settings' | 'history' | 'audit';
  /** Hide Delete/Duplicate (the details pane overflow owns those). */
  hideChromeActions?: boolean;
  /** After a successful save, stay mounted and notify the parent instead of closing. */
  stayOpenOnSave?: boolean;
  onSaved?: () => void;
  /** When set, Save / Delete / Duplicate render here instead of inside the form. */
  actionsPortal?: HTMLElement | null;
  /** Dashboard reporting period — history highlights the matching row as "This period". */
  viewingPeriod?: {
    period_year: number;
    period_month: number | null;
    period_week: number | null;
  } | null;
  /**
   * The details pane's "This period" field. When the user has edited it, Save
   * persists this value (it wins over a history-grid edit for the same period).
   */
  periodEntry?: {
    period_year: number;
    period_month: number | null;
    period_week: number | null;
    value: number | null;
    error: string | null;
  } | null;
  onDraftChartTypeChange?: (chartType: CardChartType | null) => void;
  /** When set, select this settings tab once the node has loaded. */
  initialTabLabel?: string;
  /** Nested cards/groups can jump to the dashboard's own Reminders tab. */
  onOpenDashboardReminders?: () => void;
  /** Parent binds this to Back so unsaved reminder edits are guarded. */
  onBindRequestClose?: (requestClose: () => void) => void;
  /** Close the whole details pane (panel X), not just leave settings. */
  onClosePanel?: () => void;
  onBindRequestPanelClose?: (requestClose: () => void) => void;
}

const LEVEL_OPTIONS: { value: Level; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'substantial', label: 'Substantial' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'limited', label: 'Limited' },
  { value: 'none', label: 'None' }
];

const AGGREGATION_OPTIONS: { value: AggregationType; label: string }[] = [
  { value: 'average', label: 'Average' },
  { value: 'min', label: 'Minimum' }
];

const DATA_VALUE_TYPE_OPTIONS: { value: DataValueType; label: string }[] = [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'number', label: 'Number' }
];

const BOUNDARY_DIRECTION_OPTIONS: { value: BoundaryDirection; label: string; description: string }[] = [
  { value: 'descending', label: 'Higher is better', description: 'e.g., completion rate, satisfaction score' },
  { value: 'ascending', label: 'Lower is better', description: 'e.g., defect count, incident rate' }
];

const CALCULATION_MODE_OPTIONS: {
  value: LevelCalculationMode;
  label: string;
  description: string;
  example: string;
}[] = [
  {
    value: 'absolute',
    label: 'Absolute Thresholds',
    description: 'Compare value directly against fixed thresholds',
    example: 'e.g., score ≥ 90 → High'
  },
  {
    value: 'deviation_bands',
    label: 'Deviation Bands (±%)',
    description: 'Value must be within ±X% of base to achieve level. Closer to target = better.',
    example: 'e.g., within ±3% of budget → High, within ±5% → Substantial'
  },
  {
    value: 'deviation_direct',
    label: 'Deviation (direct comparison)',
    description: 'Compare actual % of base against thresholds. You choose if higher or lower is better.',
    example: 'e.g., ≥150% of target → High (sales), or ≤50% of baseline → High (failures)'
  }
];

const BASE_VALUE_TYPE_OPTIONS: { value: BaseValueType; label: string; description: string }[] = [
  { value: 'fixed', label: 'Fixed Base', description: 'Single base value for all periods' },
  { value: 'monthly', label: 'Monthly Base', description: 'Different base value per month' },
  { value: 'rolling_average', label: 'Rolling Average', description: 'Average of last N months as base' }
];

// Default threshold values for each calculation mode.
// Only the top four levels have numeric thresholds; `none` is always the
// catch-all (anything that didn't qualify above lands here) and is saved as
// null, so its default is the empty string.
const DEFAULT_THRESHOLDS: Record<LevelCalculationMode, Record<Level, string>> = {
  absolute: {
    high: '90',
    substantial: '75',
    moderate: '50',
    limited: '25',
    none: '',
    nan: ''
  },
  deviation_bands: {
    // ±3%, ±5%, ±10%, ±15%; anything past Limited falls into None as catch-all.
    high: '3',
    substantial: '5',
    moderate: '10',
    limited: '15',
    none: '',
    nan: ''
  },
  deviation_direct: {
    // 150% / 125% / 100% / 75% of base (higher is better). Below Limited → None.
    high: '150',
    substantial: '125',
    moderate: '100',
    limited: '75',
    none: '',
    nan: ''
  }
};

// Inverted thresholds for "lower is better" in absolute mode.
const DEFAULT_THRESHOLDS_ABSOLUTE_ASCENDING: Record<Level, string> = {
  high: '10',
  substantial: '25',
  moderate: '50',
  limited: '75',
  none: '',
  nan: ''
};

// Inverted thresholds for "lower is better" in direct mode.
// 50% / 75% / 100% / 125% of base; anything past Limited → None catch-all.
const DEFAULT_THRESHOLDS_DIRECT_ASCENDING: Record<Level, string> = {
  high: '50',
  substantial: '75',
  moderate: '100',
  limited: '125',
  none: '',
  nan: ''
};

const UPDATE_CADENCE_OPTIONS: { value: UpdateCadence; label: string; description: string }[] = [
  { value: 'monthly', label: 'Monthly', description: 'Data is entered and tracked monthly' },
  { value: 'quarterly', label: 'Quarterly', description: 'Data is entered quarterly (fiscal year starting April)' },
  { value: 'weekly', label: 'Weekly', description: 'Data is entered and tracked per ISO week (Monday to Sunday)' }
];

const GROUP_NUMERIC_DISPLAY_TYPE_OPTIONS: { value: GroupNumericDisplayType; label: string; description: string }[] = [
  {
    value: 'assurance_score',
    label: 'Assurance score (1 to 5)',
    description: "Show the ordinal rank of this group's aggregated assurance level"
  },
  {
    value: 'child_score_aggregate',
    label: "Children's scores (aggregated)",
    description:
      "Show a sum, average, min, or max of direct children's numeric values (data cards and group cards with a display number)"
  },
  {
    value: 'pinned_child',
    label: 'A specific child card',
    description:
      'Show the current value of one selected child card (data cards, or group cards that also display a number)'
  }
];

const CHILD_SCORE_AGGREGATION_OPTIONS: { value: ChildScoreAggregation; label: string }[] = [
  { value: 'average', label: 'Average' },
  { value: 'sum', label: 'Sum' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' }
];

interface PendingDataValue {
  period_year: number;
  period_month: number | null;
  period_week: number | null;
  value: number | null;
}

export default function NodeDetailModal({
  nodeId,
  scopeId,
  isOpen,
  onClose,
  onDeleted,
  readOnly = false,
  dataEntryOnly = false,
  groupChildren = [],
  chrome = 'modal',
  sections = 'full',
  hideChromeActions = false,
  stayOpenOnSave = false,
  onSaved,
  actionsPortal = null,
  viewingPeriod,
  periodEntry = null,
  onDraftChartTypeChange,
  initialTabLabel,
  onOpenDashboardReminders,
  onBindRequestClose,
  onClosePanel,
  onBindRequestPanelClose
}: NodeDetailModalProps) {
  const useChartTiles = useChartTilesEnabled();
  const navigate = useTenantNavigate();
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: node, isLoading: nodeLoading } = useMetricNode(nodeId);
  const { data: dataValues, isLoading: valuesLoading } = useDataValues(nodeId);
  const { data: boundaries, isLoading: boundariesLoading } = useLevelBoundaries(nodeId);
  const { data: baseValues, isLoading: baseValuesLoading } = useBaseValues(nodeId);
  const { data: linkedDashboards, isLoading: linkedDashboardsLoading } = useLinkedDashboards(nodeId, isOpen);
  const { data: availableParents, isLoading: parentsLoading } = useAvailableParents(
    nodeId,
    isOpen && !readOnly && !dataEntryOnly
  );
  const [auditTrailTabVisited, setAuditTrailTabVisited] = useState(false);
  const canFetchAuditLogs =
    (auditTrailTabVisited || sections === 'audit') &&
    (node?.type === 'data_card' || node?.type === 'status_card') &&
    node?.user_permission === ResourcePermissionRole.OWNER;
  const { data: auditLogData, isLoading: auditLogsLoading } = useNodeAuditLogs(nodeId, canFetchAuditLogs);
  const shareNodeMutation = useShareNode();

  const { data: nodePermissions } = useNodePermissions(scopeId);
  const currentSharesForMentions = useMemo(
    () => nodePermissions?.shared_with?.map((s) => ({ email: s.email, role: s.permission })) || [],
    [nodePermissions]
  );
  const mentionShareFlow = useMentionShareFlow(currentSharesForMentions, ResourcePermissionRole.DATA_ENTRY);

  const updateNodeMutation = useUpdateMetricNode();
  const deleteNodeMutation = useDeleteMetricNode();
  const duplicateCardMutation = useDuplicateCard();
  const upsertDataValueMutation = useUpsertDataValue();
  const deleteDataValueMutation = useDeleteDataValue();
  const upsertBoundariesMutation = useUpsertLevelBoundaries();
  const upsertBaseValueMutation = useUpsertBaseValue();
  const deleteBaseValueMutation = useDeleteBaseValue();

  // Form state
  const [nodeName, setNodeName] = useState('');
  const [aggregationType, setAggregationType] = useState<AggregationType>('average');
  // Group card numeric display state
  const [groupNumericDisplayType, setGroupNumericDisplayType] = useState<GroupNumericDisplayType | null>(null);
  const [groupNumericChildAggregation, setGroupNumericChildAggregation] = useState<ChildScoreAggregation>('average');
  const [pinnedChildId, setPinnedChildId] = useState<string | null>(null);
  const [dataValueType, setDataValueType] = useState<DataValueType>('percentage');
  const [boundaryDirection, setBoundaryDirection] = useState<BoundaryDirection>('descending');
  const [updateCadence, setUpdateCadence] = useState<UpdateCadence>('monthly');
  // null = the card type's default chart; only set once the user picks one
  const [chartType, setChartType] = useState<CardChartType | null>(null);
  const [levelBoundaries, setLevelBoundaries] = useState<Record<Level, string>>(DEFAULT_THRESHOLDS.absolute);

  // Calculation mode state - simplified to 3 options
  const [selectedMode, setSelectedMode] = useState<LevelCalculationMode>('absolute');

  // Track whether the user has typed into the threshold inputs during this
  // modal session. Used together with the saved-boundary signal below to
  // decide when mode/direction changes are allowed to overwrite the current
  // thresholds with defaults. Sessions starts as `false` and flips to `true`
  // on any explicit edit.
  const [, setThresholdsUserEditedSession] = useState(false);
  const [baseValueType, setBaseValueType] = useState<BaseValueType>('fixed');
  // Starts unset; the user must pick a window when they choose Rolling Average.
  const [rollingAverageMonths, setRollingAverageMonths] = useState<number | null>(null);

  // Empty defaults for absolute-mode "number" cards — the 90/75/50/25/0
  // defaults are tuned for percentages and are meaningless for a card whose
  // values are e.g. defect counts in [0..1000]. Force the user to pick their
  // own scale.
  const EMPTY_ABSOLUTE_THRESHOLDS: Record<Level, string> = {
    high: '',
    substantial: '',
    moderate: '',
    limited: '',
    none: '',
    nan: ''
  };

  // Handler to change mode and update default thresholds. Mode changes
  // always overwrite the threshold inputs with the new mode's defaults —
  // unlike a direction flip, the unit of measurement is different across
  // modes (raw value vs ±% deviation band vs % of base), so leaving the
  // previous mode's values would render a card with thresholds that the
  // calc semantically can't interpret (and the ordering validator would
  // flag immediately). Users can override after the swap if they want
  // custom thresholds for the new mode.
  const handleModeChange = (newMode: LevelCalculationMode) => {
    if (newMode === selectedMode) return;
    setSelectedMode(newMode);

    // deviation_bands is always interpreted as ascending on the backend; pin
    // the FE direction to ascending too so saved/loaded state stays in sync.
    if (newMode === 'deviation_bands' && boundaryDirection !== 'ascending') {
      setBoundaryDirection('ascending');
    }

    if (newMode === 'absolute' && dataValueType === 'number') {
      // Don't suggest percentage-shaped defaults for raw number cards.
      setLevelBoundaries(EMPTY_ABSOLUTE_THRESHOLDS);
    } else if (newMode === 'absolute' && boundaryDirection === 'ascending') {
      setLevelBoundaries(DEFAULT_THRESHOLDS_ABSOLUTE_ASCENDING);
    } else if (newMode === 'deviation_direct' && boundaryDirection === 'ascending') {
      setLevelBoundaries(DEFAULT_THRESHOLDS_DIRECT_ASCENDING);
    } else {
      setLevelBoundaries(DEFAULT_THRESHOLDS[newMode]);
    }
    // The new mode's defaults count as a fresh slate, so clear the
    // session-level "user has typed into thresholds" flag and let
    // subsequent direction flips swap defaults in too.
    setThresholdsUserEditedSession(false);
    // Suppress the boundaries hydration effect for the rest of this session —
    // saved thresholds belong to the previous mode and would silently
    // overwrite the defaults we just applied.
    setModeChangedThisSession(true);
  };

  // Base value form state
  const [baseValueInput, setBaseValueInput] = useState('');
  const [baseValueYear, setBaseValueYear] = useState<number | null>(null);
  const [baseValueMonth, setBaseValueMonth] = useState<number | null>(null);
  const [pendingBaseValues, setPendingBaseValues] = useState<
    { period_year: number | null; period_month: number | null; value: number }[]
  >([]);
  const [deletedBaseValueIds, setDeletedBaseValueIds] = useState<string[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [remindersDirty, setRemindersDirty] = useState(false);
  const reminderEditorRef = useRef<ReminderEditor | null>(null);
  const remindersTabIndexRef = useRef(-1);
  const pendingLeaveRef = useRef<'close' | 'close-panel' | 'dashboard-reminders'>('close');
  const handleReminderDirty = useCallback((dirty: boolean) => {
    setRemindersDirty((prev) => (prev === dirty ? prev : dirty));
  }, []);
  const handleRegisterReminderEditor = useCallback((editor: ReminderEditor | null) => {
    reminderEditorRef.current = editor;
  }, []);
  // When the user changes data_value_type / level_calculation_mode /
  // update_cadence on a card with saved data values, we pause the save and
  // ask them to keep / wipe / cancel — see handleSaveAll. `null` means no
  // prompt is open.
  const [pendingTypeChange, setPendingTypeChange] = useState<null | {
    fields: string[];
    valueCount: number;
  }>(null);
  const [typeChangeChoice, setTypeChangeChoice] = useState<'keep' | 'wipe' | null>(null);
  // Set by handleModeChange so the boundaries hydration effect knows to stop
  // re-applying saved thresholds — those values were tuned for the previous
  // mode and no longer make semantic sense under the new one.
  const [modeChangedThisSession, setModeChangedThisSession] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [pendingDataValues, setPendingDataValues] = useState<PendingDataValue[]>([]);
  const [deletedSavedValueIds, setDeletedSavedValueIds] = useState<string[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);

  // Validation state
  const [thresholdErrors, setThresholdErrors] = useState<ValidationError[]>([]);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);

  const isDataCard = node?.type === 'data_card';
  const isGroupCard = node?.type === 'group_card';
  const isStatusCard = node?.type === 'status_card';
  const hasDataValues = isDataCard || isStatusCard;

  useEffect(() => {
    if (!onDraftChartTypeChange || !node) return;
    const resolved = resolveChartType(chartType, node.type);
    onDraftChartTypeChange(resolved);
  }, [chartType, node, onDraftChartTypeChange]);

  useEffect(() => {
    return () => {
      onDraftChartTypeChange?.(null);
    };
  }, [onDraftChartTypeChange]);

  interface OrgUser {
    user_id: number;
    email: string;
    firstname?: string | null;
    surname?: string | null;
  }

  const { data: orgUsers } = useQuery({
    queryKey: ['account-users-reporting'],
    queryFn: async () => {
      const response = await apiGet<OrgUser[]>('/tenant-management/members', {
        meta: { globalError: false, context: 'reporting-activity' }
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  const getUserDisplayName = useCallback(
    (userId: number | null | undefined) => {
      if (!userId) return 'System';
      const user = orgUsers?.find((u: OrgUser) => u.user_id === userId);
      if (!user) return 'Unknown User';
      if (user.firstname && user.surname) return `${user.firstname} ${user.surname}`;
      return user.email;
    },
    [orgUsers]
  );

  // Load initial values once per card. Depend on node.id, not `node` — a
  // refetch returns a new object and would overwrite in-progress edits
  // (calculation mode especially) with the still-unsaved server value.
  useEffect(() => {
    if (node) {
      setNodeName(node.title);
      setChartType(node.chart_type ?? null);
      if (node.aggregation_type) {
        setAggregationType(node.aggregation_type);
      }
      if (node.type === 'group_card') {
        setGroupNumericDisplayType(node.group_numeric_display_type ?? null);
        setGroupNumericChildAggregation(node.group_numeric_child_aggregation ?? 'average');
        setPinnedChildId(node.pinned_child_id ?? null);
      }
      if (node.data_value_type) {
        setDataValueType(node.data_value_type);
      }
      if (node.boundary_direction) {
        setBoundaryDirection(node.boundary_direction);
      }
      // Set update cadence from backend
      if (node.update_cadence) {
        setUpdateCadence(node.update_cadence);
      }
      // Set selected mode from backend (now direct mapping)
      if (node.level_calculation_mode) {
        setSelectedMode(node.level_calculation_mode);
      } else {
        setSelectedMode('absolute');
      }
      if (node.base_value_type) {
        setBaseValueType(node.base_value_type);
      }
      if (node.rolling_average_months) {
        setRollingAverageMonths(node.rolling_average_months);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate on card change only
  }, [node?.id]);

  // Hydrate threshold inputs from saved boundaries. Start from empty strings
  // rather than mode defaults (90/75/50/25) so a missing Moderate isn't filled
  // with 50 and then flagged as out of order on lower-is-better cards.
  // After `handleModeChange` fires we skip the re-hydration — the
  // mode-change handler owns the threshold values from that point on, and
  // re-running this effect would silently undo its work.
  useEffect(() => {
    if (modeChangedThisSession) return;
    if (boundaries?.boundaries && boundaries.boundaries.length > 0) {
      const boundaryMap: Record<Level, string> = { ...EMPTY_ABSOLUTE_THRESHOLDS };
      boundaries.boundaries.forEach((b) => {
        if (b.level !== 'none' && b.threshold_value !== null) {
          boundaryMap[b.level] = String(b.threshold_value);
        }
      });
      setLevelBoundaries(boundaryMap);
    }
  }, [boundaries, modeChangedThisSession]);

  // Reset pending values when modal opens
  useEffect(() => {
    if (isOpen) {
      setPendingDataValues([]);
      setDeletedSavedValueIds([]);
      setPendingBaseValues([]);
      setDeletedBaseValueIds([]);
      setShowUnsavedConfirm(false);
      setPendingTypeChange(null);
      setTypeChangeChoice(null);
      setModeChangedThisSession(false);
      // Reset the session-level "user has typed into thresholds" flag.
      setThresholdsUserEditedSession(false);
    }
  }, [isOpen]);

  // Initialize selected parent when available parents data loads
  useEffect(() => {
    if (availableParents?.current_parent_id) {
      setSelectedParentId(availableParents.current_parent_id);
    }
  }, [availableParents?.current_parent_id]);

  // Compare current config edit-form state against the loaded node so the
  // unsaved-changes guard catches the most common edit paths (title rename,
  // direction flip, threshold retype, aggregation/mode change, …), not just
  // pending data values.
  const configDirty = (() => {
    if (!node) return false;
    if (nodeName.trim() !== (node.title ?? '')) return true;
    if (chartType !== (node.chart_type ?? null)) return true;
    if (isGroupCard && node.aggregation_type && aggregationType !== node.aggregation_type) return true;
    if (isGroupCard) {
      if (groupNumericDisplayType !== (node.group_numeric_display_type ?? null)) return true;
      if (
        groupNumericDisplayType === 'child_score_aggregate' &&
        groupNumericChildAggregation !== (node.group_numeric_child_aggregation ?? 'average')
      )
        return true;
      if (groupNumericDisplayType === 'pinned_child' && pinnedChildId !== (node.pinned_child_id ?? null)) return true;
    }
    if (isDataCard) {
      if (node.data_value_type && dataValueType !== node.data_value_type) return true;
      if (node.boundary_direction && boundaryDirection !== node.boundary_direction) return true;
      if (node.level_calculation_mode && selectedMode !== node.level_calculation_mode) return true;
      if (selectedMode !== 'absolute') {
        if (node.base_value_type && baseValueType !== node.base_value_type) return true;
        if (baseValueType === 'rolling_average' && rollingAverageMonths !== (node.rolling_average_months ?? null)) {
          return true;
        }
      }
    }
    if (hasDataValues && node.update_cadence && updateCadence !== node.update_cadence) return true;
    // Compare current threshold inputs against saved boundaries.
    if (isDataCard && boundaries?.boundaries) {
      const savedMap: Partial<Record<Level, string>> = {};
      for (const b of boundaries.boundaries) {
        savedMap[b.level] = b.threshold_value === null ? '' : String(b.threshold_value);
      }
      for (const opt of LEVEL_OPTIONS) {
        const current = (levelBoundaries[opt.value] ?? '').trim();
        const saved = (savedMap[opt.value] ?? '').trim();
        if (current !== saved) return true;
      }
    }
    return false;
  })();

  const hasUnsavedChanges =
    pendingDataValues.length > 0 ||
    deletedSavedValueIds.length > 0 ||
    pendingBaseValues.length > 0 ||
    deletedBaseValueIds.length > 0 ||
    configDirty ||
    remindersDirty ||
    (selectedParentId && selectedParentId !== availableParents?.current_parent_id) || // Add move to unsaved changes
    periodEntry != null;

  // Validate thresholds on change (ordering, mid-list gaps, mode-specific values)
  useEffect(() => {
    if (isDataCard && selectedMode && boundaryDirection) {
      const errors = validateLevelBoundaries(levelBoundaries, boundaryDirection, selectedMode);
      setThresholdErrors(errors);
    } else {
      setThresholdErrors([]);
    }
  }, [levelBoundaries, boundaryDirection, selectedMode, isDataCard]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    pendingLeaveRef.current = 'close';
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleOpenDashboardReminders = useCallback(() => {
    if (!onOpenDashboardReminders) return;
    if (hasUnsavedChanges) {
      pendingLeaveRef.current = 'dashboard-reminders';
      setShowUnsavedConfirm(true);
    } else {
      onOpenDashboardReminders();
    }
  }, [hasUnsavedChanges, onOpenDashboardReminders]);

  const handlePanelClose = useCallback(() => {
    pendingLeaveRef.current = 'close-panel';
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
    } else {
      (onClosePanel ?? onClose)();
    }
  }, [hasUnsavedChanges, onClosePanel, onClose]);

  useEffect(() => {
    onBindRequestClose?.(handleClose);
  }, [onBindRequestClose, handleClose]);

  useEffect(() => {
    onBindRequestPanelClose?.(handlePanelClose);
  }, [onBindRequestPanelClose, handlePanelClose]);

  const handleDiscardAndClose = () => {
    setShowUnsavedConfirm(false);
    setPendingDataValues([]);
    setDeletedSavedValueIds([]);
    setPendingBaseValues([]);
    setDeletedBaseValueIds([]);
    setRemindersDirty(false);
    const dest = pendingLeaveRef.current;
    pendingLeaveRef.current = 'close';
    if (dest === 'dashboard-reminders') onOpenDashboardReminders?.();
    else if (dest === 'close-panel') (onClosePanel ?? onClose)();
    else onClose();
  };

  // Handle import preview - populate pending data values from imported data
  const handleImportPreview = (data: ImportPreviewResponse) => {
    const newPendingValues: PendingDataValue[] = [...pendingDataValues];

    for (const importValue of data.data_values) {
      if (importValue.value !== null && !importValue.validation_error) {
        // A weekly row carries period_week instead of period_month, so
        // matching on period_month alone would collide two different weekly
        // rows that both happen to have period_month=null.
        const existingIndex = newPendingValues.findIndex(
          (v) =>
            v.period_year === importValue.period_year &&
            v.period_month === importValue.period_month &&
            v.period_week === importValue.period_week
        );

        if (existingIndex >= 0) {
          // Update existing pending value
          newPendingValues[existingIndex] = {
            ...newPendingValues[existingIndex],
            value: importValue.value
          };
        } else {
          // Add new pending value
          newPendingValues.push({
            period_year: importValue.period_year,
            period_month: importValue.period_month,
            period_week: importValue.period_week,
            value: importValue.value
          });
        }
      }
    }

    setPendingDataValues(newPendingValues);
    toast.success(`Loaded ${data.cards_with_new_values} value${data.cards_with_new_values > 1 ? 's' : ''} from import`);
  };

  const handleSaveAll = async (typeChangeOverride?: 'keep' | 'wipe') => {
    if (!nodeName.trim()) return;

    if (remindersDirty) {
      const editor = reminderEditorRef.current;
      if (!editor || !editor.validate()) {
        const idx = remindersTabIndexRef.current;
        if (idx >= 0) setActiveTabIndex(idx);
        return;
      }
    }

    // The override lets the type-change prompt resume the save without
    // relying on `typeChangeChoice` state — `handleSaveAll` is captured by
    // the prompt's button at render time, so a freshly-set state value
    // wouldn't be visible to the closure that re-invokes it.
    const effectiveTypeChangeChoice: 'keep' | 'wipe' | null = typeChangeOverride ?? typeChangeChoice;

    // Check for validation errors before saving (skip if data entry only — rules aren't being saved)
    if (isDataCard && !dataEntryOnly && thresholdErrors.length > 0) {
      toast.error(`Threshold validation error: ${thresholdErrors[0].message}`);
      setActiveTabIndex(1); // Switch to Rules tab
      return;
    }

    // If the user is changing data_value_type, level_calculation_mode, or
    // update_cadence and saved data values already exist, the existing rows
    // are no longer guaranteed to be valid against the new config. Pause and
    // ask: keep the rows (they'll need review), wipe them, or cancel.
    // Resume via the prompt's Keep / Wipe handlers below, which call
    // handleSaveAll with the appropriate override.
    if (
      !dataEntryOnly &&
      node &&
      isDataCard &&
      (dataValues?.data_values?.length ?? 0) > 0 &&
      effectiveTypeChangeChoice === null
    ) {
      const typeChanged = node.data_value_type && dataValueType !== node.data_value_type;
      const modeChanged = node.level_calculation_mode && selectedMode !== node.level_calculation_mode;
      const cadenceChanged = node.update_cadence && updateCadence !== node.update_cadence;
      if (typeChanged || modeChanged || cadenceChanged) {
        const fields: string[] = [];
        if (typeChanged) fields.push('value type');
        if (modeChanged) fields.push('calculation mode');
        if (cadenceChanged) fields.push('update cadence');
        setPendingTypeChange({ fields, valueCount: dataValues!.data_values!.length });
        return; // Save resumes via the prompt's Keep / Wipe handlers below.
      }
    }

    // Gate deviation-mode save on required fields so the user gets an inline
    // explanation instead of a generic backend 400.
    if (isDataCard && !dataEntryOnly && selectedMode !== 'absolute') {
      if (!baseValueType) {
        toast.error('Choose how to determine the base value (Fixed / Monthly / Rolling Average) before saving.');
        setActiveTabIndex(1);
        return;
      }
      if (baseValueType === 'rolling_average') {
        if (rollingAverageMonths === null || rollingAverageMonths < 1 || rollingAverageMonths > 24) {
          toast.error('Rolling Average needs a window. Pick a number of months between 1 and 24.');
          setActiveTabIndex(1);
          return;
        }
      }
    }

    if (periodEntry?.error) {
      toast.error(periodEntry.error);
      return;
    }

    setIsSaving(true);
    try {
      // 1. Save node details (name, aggregation type, data value type, calculation mode)
      // Skip structural changes if user only has data entry permission
      if (!dataEntryOnly) {
        const updateData: any = { title: nodeName.trim() };

        if (isGroupCard) {
          updateData.aggregation_type = aggregationType;
          updateData.group_numeric_display_type = groupNumericDisplayType;
          updateData.group_numeric_child_aggregation =
            groupNumericDisplayType === 'child_score_aggregate' ? groupNumericChildAggregation : null;
          updateData.pinned_child_id = groupNumericDisplayType === 'pinned_child' ? pinnedChildId : null;
        }

        if (isDataCard) {
          updateData.data_value_type = dataValueType;
          updateData.boundary_direction = boundaryDirection;
          updateData.level_calculation_mode = selectedMode;

          // Only set deviation-related fields if not using absolute mode
          if (selectedMode !== 'absolute') {
            updateData.base_value_type = baseValueType;
            if (baseValueType === 'rolling_average' && rollingAverageMonths !== null) {
              updateData.rolling_average_months = rollingAverageMonths;
            }
          }
        }

        // Update cadence is persisted for anything that records values per period
        // (data cards and status cards), since it controls the reporting period grid.
        if (hasDataValues) {
          updateData.update_cadence = updateCadence;
        }

        if (chartType !== (node?.chart_type ?? null)) {
          updateData.chart_type = chartType;
        }

        // Add parent_id to update if it changed (move operation)
        if (selectedParentId && selectedParentId !== availableParents?.current_parent_id) {
          updateData.parent_id = selectedParentId;
        }

        await updateNodeMutation.mutateAsync({
          nodeId,
          data: updateData
        });
      }

      if (remindersDirty) {
        await reminderEditorRef.current?.save();
      }

      // Sequential per-row mutations would each refetch the full dashboard
      // tree (flicker + N round-trips). Suppress invalidation inside the loop
      // and invalidate once at the end.
      let mutationsRan = false;

      // When the user changes cadence from monthly → quarterly AND chose
      // "Keep values" in the #1278 prompt, normalise the saved monthly rows
      // to their quarter-end equivalents so they stay visible and pass the
      // backend cadence guard. Collisions (multiple monthly rows in the
      // same fiscal quarter) collapse to one — keep the most recent by
      // `updated_at`; the others get deleted.
      const cadenceSwitchedToQuarterly =
        hasDataValues &&
        !dataEntryOnly &&
        node?.update_cadence === 'monthly' &&
        updateCadence === 'quarterly' &&
        effectiveTypeChangeChoice === 'keep';
      const extraDeleteIds: string[] = [];
      const cadenceMigrations: DataValueUpsert[] = [];
      if (cadenceSwitchedToQuarterly && dataValues?.data_values) {
        const winnerByQuarter = new Map<
          string,
          { id: string; updatedAt: string; value: number; snappedMonth: number; year: number }
        >();
        for (const row of dataValues.data_values) {
          // This branch only ever runs for cards that were monthly (see
          // cadenceSwitchedToQuarterly above), which always have a
          // period_month; the null case is only reachable for weekly rows.
          if (row.period_month === null) continue;
          if (QUARTER_END_MONTHS.includes(row.period_month as 3 | 6 | 9 | 12)) continue;
          const quarter = getQuarterFromMonth(row.period_month);
          const snappedMonth = QUARTER_END_MONTHS[quarter - 1];
          const key = `${row.period_year}-${snappedMonth}`;
          const incumbent = winnerByQuarter.get(key);
          if (!incumbent || row.updated_at > incumbent.updatedAt) {
            if (incumbent) extraDeleteIds.push(incumbent.id);
            winnerByQuarter.set(key, {
              id: row.id,
              updatedAt: row.updated_at,
              value: row.value,
              snappedMonth,
              year: row.period_year
            });
          } else {
            extraDeleteIds.push(row.id);
          }
        }
        for (const winner of winnerByQuarter.values()) {
          extraDeleteIds.push(winner.id);
          cadenceMigrations.push({
            period_year: winner.year,
            period_month: winner.snappedMonth,
            value: winner.value
          });
        }
      }

      // The details pane's "This period" field is a second editor for the
      // viewing period. When it has a value, it replaces any grid edit for
      // that same period so Save stores what the field shows.
      const sameAsPeriodEntry = (row: {
        period_year: number;
        period_month: number | null;
        period_week: number | null;
      }) =>
        periodEntry?.value != null &&
        row.period_year === periodEntry.period_year &&
        (row.period_month ?? null) === periodEntry.period_month &&
        (row.period_week ?? null) === periodEntry.period_week;
      // The later upsert updates this period's saved row in place. Deleting
      // it first commits before that upsert is validated, so a failure would
      // drop the old value. Cadence-migration ids stay in extraDeleteIds.
      const periodEntrySavedId =
        periodEntry?.value != null ? dataValues?.data_values?.find((row) => sameAsPeriodEntry(row))?.id : undefined;

      // 2. For data cards and status cards, delete removed data values
      const deletesToRun = [...deletedSavedValueIds.filter((id) => id !== periodEntrySavedId), ...extraDeleteIds];
      if (hasDataValues && deletesToRun.length > 0) {
        for (const dataValueId of deletesToRun) {
          await deleteDataValueMutation.mutateAsync({
            nodeId,
            dataValueId,
            skipInvalidation: true
          });
          mutationsRan = true;
        }
      }

      // 2b. Re-upsert any cadence-migrated rows at their quarter-end period.
      if (cadenceMigrations.length > 0) {
        for (const migration of cadenceMigrations) {
          await upsertDataValueMutation.mutateAsync({
            nodeId,
            data: migration,
            skipInvalidation: true
          });
          mutationsRan = true;
        }
      }

      // 3. For data cards and status cards, handle pending data values.
      // A This-period value wins over a history-grid edit for that period.
      const valuesToSave: PendingDataValue[] = pendingDataValues.filter((pending) => !sameAsPeriodEntry(pending));
      if (periodEntry?.value != null) {
        valuesToSave.push({
          period_year: periodEntry.period_year,
          period_month: periodEntry.period_month,
          period_week: periodEntry.period_week,
          value: periodEntry.value
        });
      }
      if (hasDataValues && valuesToSave.length > 0) {
        for (const pendingValue of valuesToSave) {
          if (pendingValue.value !== null) {
            // Save values that have a value
            await upsertDataValueMutation.mutateAsync({
              nodeId,
              data: {
                period_year: pendingValue.period_year,
                period_month: pendingValue.period_month,
                period_week: pendingValue.period_week,
                value: pendingValue.value
              },
              skipInvalidation: true
            });
            mutationsRan = true;
          } else {
            // If value is null/empty, check if there's a saved value to delete.
            // A weekly row has period_month=null, so period_week must match
            // too or every weekly row would match on the first comparison.
            const existingSaved = dataValues?.data_values?.find(
              (v) =>
                v.period_year === pendingValue.period_year &&
                v.period_month === pendingValue.period_month &&
                v.period_week === pendingValue.period_week
            );
            if (existingSaved && !deletedSavedValueIds.includes(existingSaved.id)) {
              // Delete the saved value since it's now empty
              await deleteDataValueMutation.mutateAsync({
                nodeId,
                dataValueId: existingSaved.id,
                skipInvalidation: true
              });
              mutationsRan = true;
            }
          }
        }
      }

      if (mutationsRan) {
        queryClient.invalidateQueries({ queryKey: ['nodes', nodeId, 'data'] });
        queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      }

      // 4. For data cards, also save boundaries (requires full write permission).
      // The bottom level (`none`) is always saved as null — it's the catch-all
      // band, not a fifth threshold the user needs to fill in. The calc path
      // treats null as "everything that didn't qualify above lands here".
      if (isDataCard && !dataEntryOnly) {
        const boundaryInputs: LevelBoundaryInput[] = LEVEL_OPTIONS.map((opt) => {
          if (opt.value === 'none') {
            return { level: opt.value, threshold_value: null };
          }
          const parsed = parseThresholdInput(levelBoundaries[opt.value]);
          return {
            level: opt.value,
            threshold_value: parsed
          };
        });
        await upsertBoundariesMutation.mutateAsync({
          nodeId,
          data: { boundaries: boundaryInputs }
        });
      }

      // 5. For data cards with deviation mode, handle base values (requires full write permission)
      if (isDataCard && selectedMode !== 'absolute' && !dataEntryOnly) {
        // Delete removed base values
        for (const baseValueId of deletedBaseValueIds) {
          await deleteBaseValueMutation.mutateAsync({
            nodeId,
            baseValueId
          });
        }

        // Save pending base values
        for (const pendingBase of pendingBaseValues) {
          await upsertBaseValueMutation.mutateAsync({
            nodeId,
            data: {
              period_year: pendingBase.period_year,
              period_month: pendingBase.period_month,
              value: pendingBase.value
            }
          });
        }
      }

      // Show success message
      if (selectedParentId && selectedParentId !== availableParents?.current_parent_id) {
        const newParent = availableParents?.parents.find((p) => p.id === selectedParentId);
        const currentParent = availableParents?.current_parent_id
          ? availableParents?.parents.find((p) => p.id === availableParents.current_parent_id)
          : null;

        if (newParent) {
          // Check if it's a cross-dashboard move by comparing dashboard IDs
          const isCrossDashboard = currentParent && newParent.dashboard_id !== currentParent.dashboard_id;

          if (isCrossDashboard) {
            // Cross-dashboard move
            const groupName = newParent.id === newParent.dashboard_id ? 'root' : newParent.title;
            toast.success(`Card saved and moved to "${groupName}" in dashboard "${newParent.dashboard_name}"`);
          } else {
            // Same dashboard move
            const groupName = newParent.id === newParent.dashboard_id ? 'root' : newParent.title;
            toast.success(`Card saved and moved to "${groupName}"`);
          }
        } else {
          toast.success('Card saved successfully');
        }
      } else {
        toast.success('Card saved successfully');
      }

      if (node && chartType !== (node.chart_type ?? null)) {
        posthog?.capture('assurance_card_chart_type_changed', {
          dashboard_id: scopeId,
          node_id: nodeId,
          node_type: node.type,
          from_chart_type: resolveChartType(node.chart_type, node.type),
          to_chart_type: resolveChartType(chartType, node.type)
        });
      }

      // Track save event with details of what was changed
      posthog?.capture('assurance_node_saved', {
        dashboard_id: scopeId,
        node_id: nodeId,
        node_type: node?.type,
        data_values_added: valuesToSave.filter((value) => value.value !== null).length,
        data_values_deleted: deletedSavedValueIds.length,
        base_values_added: pendingBaseValues.length,
        base_values_deleted: deletedBaseValueIds.length,
        calculation_mode: isDataCard ? selectedMode : undefined,
        boundary_direction: isDataCard ? boundaryDirection : undefined,
        was_moved: selectedParentId !== availableParents?.current_parent_id,
        new_parent_id: selectedParentId !== availableParents?.current_parent_id ? selectedParentId : undefined,
        chart_type: node ? resolveChartType(chartType, node.type) : undefined,
        chart_type_changed: chartType !== (node?.chart_type ?? null),
        update_cadence: hasDataValues ? updateCadence : undefined,
        cadence_changed: hasDataValues && !!node?.update_cadence && node.update_cadence !== updateCadence,
        weekly_values_added: valuesToSave.filter((value) => value.value !== null && value.period_week != null).length
      });

      onSaved?.();
      if (stayOpenOnSave) {
        setPendingDataValues([]);
        setDeletedSavedValueIds([]);
        setPendingBaseValues([]);
        setDeletedBaseValueIds([]);
      } else {
        onClose();
      }
    } catch (error: any) {
      // Surface the actual backend message when present so users can act on
      // validation/permission failures instead of staring at a generic toast.
      //
      // Pydantic field validators raise `ValueError`, which FastAPI wraps as
      // `"Value error, <our string>"` (and pydantic-core sometimes uses
      // "Assertion failed, ..."). Strip those prefixes so users see the
      // friendly message we authored, not pydantic plumbing.
      const cleanMessage = (raw: string): string => raw.replace(/^(Value error|Assertion failed|Type error),\s*/i, '');
      const detail = error?.response?.data?.detail;
      let backendMessage: string | undefined;
      if (typeof detail === 'string') {
        backendMessage = cleanMessage(detail);
      } else if (Array.isArray(detail) && detail.length > 0) {
        backendMessage = detail
          .map((d) => (typeof d === 'string' ? d : d?.msg || d?.message))
          .filter(Boolean)
          .map((s: string) => cleanMessage(s))
          .join('; ');
      } else if (detail && typeof detail === 'object') {
        const raw = detail.msg || detail.message;
        backendMessage = raw ? cleanMessage(raw) : undefined;
      }
      toast.error(backendMessage || error?.message || 'Failed to save card. Please try again.');
    } finally {
      setIsSaving(false);
      setTypeChangeChoice(null);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteNodeMutation.mutateAsync(nodeId);
      posthog?.capture('assurance_node_deleted', {
        dashboard_id: scopeId,
        node_id: nodeId,
        node_type: node?.type,
        node_name: node?.title
      });
      onDeleted?.();
      onClose();
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDuplicate = async () => {
    setIsDuplicating(true);
    try {
      const result = await duplicateCardMutation.mutateAsync(nodeId);
      posthog?.capture('assurance_node_duplicated', {
        dashboard_id: scopeId,
        original_node_id: nodeId,
        new_node_id: result.root_node.new_id,
        duplicated_count: result.duplicated_count,
        node_type: node?.type,
        node_name: node?.title
      });
      toast.success(
        `Duplicated "${node?.title}" and ${result.duplicated_count - 1} children. New title: "${result.root_node.title}"`
      );
      onSaved?.();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Failed to duplicate card. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsDuplicating(false);
    }
  };

  const isLoading =
    nodeLoading ||
    (isDataCard && (valuesLoading || boundariesLoading || baseValuesLoading)) ||
    (isStatusCard && valuesLoading);

  if (!isOpen) return null;

  // Tab content components
  // Whether structural editing is allowed (details, rules, move, delete, duplicate)
  const structuralReadOnly = readOnly || dataEntryOnly;

  const detailsContent = (
    <div className="space-y-4">
      {dataEntryOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            <strong>View only</strong>: you need Edit permission to change card settings.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-2 node-name-field">
        <label className="text-sm font-semibold text-content-subtle">Name</label>
        <InputField
          value={nodeName}
          onChange={(e) => setNodeName(e.target.value)}
          placeholder="Enter name"
          fullWidth
          disabled={structuralReadOnly}
        />
      </div>

      {isGroupCard && (
        <div className="flex flex-col gap-2 aggregation-type-dropdown">
          <label className="text-sm font-semibold text-content-subtle">Aggregation Method</label>
          <Dropdown
            options={AGGREGATION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            initialValue={aggregationType}
            onChange={(v) => setAggregationType(v as AggregationType)}
            width="w-full"
            disabled={structuralReadOnly}
          />
          <p className="text-xs text-content-subtle">
            How child metrics are combined to calculate this group's assurance level
          </p>
        </div>
      )}

      {isDataCard && (
        <div className="flex flex-col gap-2 data-value-type-dropdown">
          <label className="text-sm font-semibold text-content-subtle">Value Type</label>
          <Dropdown
            options={DATA_VALUE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            initialValue={dataValueType}
            onChange={(v) => setDataValueType(v as DataValueType)}
            width="w-full"
            disabled={structuralReadOnly}
          />
          <p className="text-xs text-content-subtle">How values for this metric are displayed</p>
        </div>
      )}

      {hasDataValues && (
        <div className="flex flex-col gap-2 update-cadence-dropdown">
          <label className="text-sm font-semibold text-content-subtle">Update Frequency</label>
          <Dropdown
            options={UPDATE_CADENCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            initialValue={updateCadence}
            onChange={(v) => handleCadenceChange(v as UpdateCadence)}
            width="w-full"
            disabled={structuralReadOnly}
          />
          <p className="text-xs text-content-subtle">
            {UPDATE_CADENCE_OPTIONS.find((o) => o.value === updateCadence)?.description}
          </p>
        </div>
      )}

      {isGroupCard && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <strong>Note:</strong> Group cards don't have their own data. Their assurance level is calculated by
          aggregating its cards using the selected method.
        </div>
      )}

      {/* Move Card Section - only show if user has full write (not data entry only) */}
      {!structuralReadOnly && availableParents && availableParents.parents.length > 0 && (
        <div className="border-t pt-4 mt-4">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-content-subtle">Move to Group</label>
            <Dropdown
              options={availableParents.parents.map((p) => {
                // Format display name: "Dashboard/Group" or just "Dashboard" for root
                // If depth > 1, show "Dashboard/.../Group" to indicate intermediate levels
                let displayName: string;
                if (p.id === p.dashboard_id) {
                  displayName = p.dashboard_name;
                } else if (p.depth > 1) {
                  displayName = `${p.dashboard_name}/.../  ${'  '.repeat(p.depth - 1)}${p.title}`;
                } else {
                  displayName = `${p.dashboard_name}/${p.title}`;
                }
                return {
                  value: p.id,
                  label: `${displayName}${p.is_current_parent ? ' (current)' : ''}`
                };
              })}
              initialValue={selectedParentId || availableParents.current_parent_id || ''}
              onChange={(v) => setSelectedParentId(v as string)}
              width="w-full"
              disabled={parentsLoading}
            />
            {selectedParentId &&
              selectedParentId !== availableParents.current_parent_id &&
              (() => {
                const newParent = availableParents.parents.find((p) => p.id === selectedParentId);
                if (!newParent) return null;
                return (
                  <p className="text-xs text-blue-800 font-medium mb-1">ⓘ Card will be moved when you click Save</p>
                );
              })()}
            <div className="text-xs text-content-subtle space-y-1">
              <p className="font-medium">You can move this card to any group card in your dashboards.</p>
              <p className="italic">
                Note: You cannot move a card into itself or its own child groups. Linked groups are also excluded as
                they display children from their original group.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Only show data cards and status cards in the pinned child picker
  const pinnableChildren = groupChildren.filter(
    (c) => c.type === 'data_card' || (c.type === 'group_card' && c.groupNumericDisplayValue != null)
  );

  const displayContent = (
    <div className="space-y-4">
      {dataEntryOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            <strong>View only</strong>: you need Edit permission to change card settings.
          </p>
        </div>
      )}

      {node && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-content-subtle">Main chart</label>
          <SegmentedControl
            options={chartTypesFor(node.type).map((t) => CHART_TYPE_OPTIONS[t])}
            value={resolveChartType(chartType, node.type)}
            onChange={setChartType}
            ariaLabel="Main chart on this card"
            disabled={structuralReadOnly}
            className="self-start"
          />
          <p className="text-xs text-content-subtle">
            The chart shown under the value on this card's tile
            {!useChartTiles ? ' (applies when viewed with chart tiles)' : ''}
          </p>
        </div>
      )}

      {isGroupCard && (
        <div className="border-t pt-4 space-y-4">
          <p className="text-sm text-content-subtle">
            Optionally show a numeric value on this group card tile to give viewers a quick at-a-glance figure alongside
            the assurance level.
          </p>

          {/* Enable / disable toggle */}
          <div className="flex items-center justify-between py-2 border-b border-border-default">
            <div>
              <p className="text-sm font-semibold text-content-default">Show a numeric value</p>
              <p className="text-xs text-content-subtle">When off, only the assurance gauge is shown</p>
            </div>
            <Toggle
              checked={groupNumericDisplayType !== null}
              onChange={(checked) => {
                setGroupNumericDisplayType(checked ? 'assurance_score' : null);
              }}
              disabled={structuralReadOnly}
            />
          </div>

          {groupNumericDisplayType !== null && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-content-subtle">What to display</label>
                <Dropdown
                  options={GROUP_NUMERIC_DISPLAY_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  initialValue={groupNumericDisplayType}
                  onChange={(v) => {
                    setGroupNumericDisplayType(v as GroupNumericDisplayType);
                    if (v !== 'pinned_child') setPinnedChildId(null);
                  }}
                  width="w-full"
                  disabled={structuralReadOnly}
                />
                <p className="text-xs text-content-subtle">
                  {GROUP_NUMERIC_DISPLAY_TYPE_OPTIONS.find((o) => o.value === groupNumericDisplayType)?.description}
                </p>
              </div>

              {groupNumericDisplayType === 'child_score_aggregate' && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-content-subtle">Aggregation function</label>
                  <Dropdown
                    options={CHILD_SCORE_AGGREGATION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    initialValue={groupNumericChildAggregation}
                    onChange={(v) => setGroupNumericChildAggregation(v as ChildScoreAggregation)}
                    width="w-full"
                    disabled={structuralReadOnly}
                  />
                  <p className="text-xs text-content-subtle">
                    Includes direct data card children and any child group cards that also display a number.
                  </p>
                </div>
              )}

              {groupNumericDisplayType === 'pinned_child' && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-content-subtle">Child card to display</label>
                  {pinnableChildren.length > 0 ? (
                    <>
                      <Dropdown
                        options={pinnableChildren.map((c) => ({ value: c.id, label: c.title }))}
                        initialValue={pinnedChildId || ''}
                        onChange={(v) => setPinnedChildId(v as string)}
                        width="w-full"
                        disabled={structuralReadOnly}
                      />
                      <p className="text-xs text-content-subtle">
                        The current value of the selected child will be shown on the tile. Group cards are listed here
                        only if they also have a numeric display configured.
                      </p>
                    </>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      This group has no pinnable children. Add a data card, or configure a numeric display on a child
                      group card first.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  const dataValuesContent = (
    <div
      className={
        chrome === 'none' ? 'flex flex-col h-full min-h-0 gap-3 data-values-section' : 'space-y-4 data-values-section'
      }
    >
      <div className={chrome === 'none' ? 'flex-1 min-h-0' : 'max-h-[400px] overflow-y-auto'}>
        <DataValuesEditableTable
          savedValues={dataValues?.data_values || []}
          pendingValues={pendingDataValues}
          deletedSavedValueIds={deletedSavedValueIds}
          updateCadence={updateCadence}
          dataValueType={dataValueType}
          isStatusCard={isStatusCard}
          onPendingValuesChange={setPendingDataValues}
          onDeleteSavedValue={(id) => setDeletedSavedValueIds((prev) => [...prev, id])}
          readOnly={readOnly}
          fillHeight={chrome === 'none'}
          highlightedPeriod={viewingPeriod ?? null}
        />
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-content-subtle min-w-0">Import data via Excel template</p>
          <Button
            variant="outline"
            size="sm"
            icon={UploadSimpleIcon}
            onClick={() => setShowImportModal(true)}
            className="shrink-0"
          >
            Import
          </Button>
        </div>
      )}
    </div>
  );

  // Handle direction change.
  //
  // deviation_bands always interprets thresholds as ascending regardless of
  // boundary_direction (backend pins it), so we don't even allow the toggle
  // to run here. For absolute mode, reverse the four numeric thresholds
  // (high↔limited, substantial↔moderate); a sorted-descending list becomes
  // a sorted-ascending one. None stays blank either way — it's the catch-all
  // band, no threshold needed.
  //
  // deviation_direct gets direction-specific defaults because its semantics
  // (% of base) change meaning across directions in ways a pure reversal
  // doesn't capture.
  const handleDirectionChange = (newDirection: BoundaryDirection) => {
    if (selectedMode === 'deviation_bands') {
      // Toggle is disabled in deviation_bands mode (see render below) — guard
      // here in case it's invoked programmatically.
      return;
    }
    if (newDirection !== boundaryDirection) {
      if (selectedMode === 'absolute') {
        setLevelBoundaries((prev) => ({
          ...prev,
          high: prev.limited,
          substantial: prev.moderate,
          moderate: prev.substantial,
          limited: prev.high,
          none: ''
        }));
      } else if (selectedMode === 'deviation_direct') {
        setLevelBoundaries(
          newDirection === 'ascending' ? DEFAULT_THRESHOLDS_DIRECT_ASCENDING : DEFAULT_THRESHOLDS.deviation_direct
        );
      }
      // Suppress the saved-boundaries hydration for the rest of this session,
      // mirroring the mode-change handler. The saved thresholds belong to the
      // previous direction and would otherwise overlay our new values the
      // moment the boundaries effect re-runs.
      setThresholdsUserEditedSession(false);
      setModeChangedThisSession(true);
    }
    setBoundaryDirection(newDirection);
  };

  // Switching cadence (monthly ↔ quarterly) reshapes the period grid the
  // DataValuesEditableTable renders. Any pending data values entered under
  // the previous cadence keep their original (year, month) in state — so a
  // May entry on a monthly card stays at month=5 even after the table starts
  // iterating quarter-by-quarter (Mar/Jun/Sep/Dec). That row becomes
  // invisible in the table and, on save, the upsert is rejected by the
  // cadence guard ("quarterly cards only accept data for quarter-end months").
  //
  // Snap pending values to the cadence's representative month so they stay
  // visible after the toggle and pass the guard at save time. For
  // monthly → quarterly we collapse multiple months in the same quarter
  // (last-write-wins; keep the entry the user typed most recently —
  // pendingDataValues is append-ordered, so we walk it forwards). For
  // quarterly → monthly nothing needs snapping (3/6/9/12 are valid months).
  const handleCadenceChange = (newCadence: UpdateCadence) => {
    if (newCadence === updateCadence) return;
    const oldCadence = updateCadence;
    const involvesWeekly = newCadence === 'weekly' || oldCadence === 'weekly';
    const pendingDropped = involvesWeekly
      ? pendingDataValues.filter((pv) => (pv.period_week !== null) !== (newCadence === 'weekly')).length
      : 0;
    posthog?.capture('assurance_update_cadence_changed', {
      dashboard_id: scopeId,
      node_id: nodeId,
      node_type: node?.type,
      from_cadence: oldCadence,
      to_cadence: newCadence,
      involves_weekly: involvesWeekly,
      pending_values_dropped: pendingDropped,
      had_saved_values: (dataValues?.data_values?.length ?? 0) > 0
    });
    setUpdateCadence(newCadence);

    // A switch to/from weekly changes the period's shape entirely
    // (period_week vs. period_month) - a week number doesn't correspond to
    // any particular month or quarter, so pending rows of the old shape are
    // dropped rather than migrated, mirroring the base-value-type switch
    // below rather than the monthly<->quarterly snap.
    if (newCadence === 'weekly' || oldCadence === 'weekly') {
      setPendingDataValues((prev) => {
        const newIsWeekly = newCadence === 'weekly';
        const kept = prev.filter((pv) => (pv.period_week !== null) === newIsWeekly);
        const droppedCount = prev.length - kept.length;
        if (droppedCount > 0) {
          toast.warning(
            `${droppedCount} pending value${droppedCount === 1 ? ' was' : 's were'} cleared. Periods don't carry over when switching to or from weekly.`
          );
        }
        return kept;
      });
      return;
    }

    if (newCadence !== 'quarterly') return;

    setPendingDataValues((prev) => {
      const byCell = new Map<string, PendingDataValue>();
      let collapsedCount = 0;
      for (const pv of prev) {
        if (pv.period_month === null) continue;
        const quarter = getQuarterFromMonth(pv.period_month);
        const snappedMonth = QUARTER_END_MONTHS[quarter - 1];
        const key = `${pv.period_year}-${snappedMonth}`;
        if (byCell.has(key)) collapsedCount += 1;
        byCell.set(key, { ...pv, period_month: snappedMonth });
      }
      if (collapsedCount > 0) {
        toast.warning(
          `${collapsedCount} pending value${collapsedCount === 1 ? ' was' : 's were'} in the same quarter. Kept the most recent.`
        );
      }
      return Array.from(byCell.values());
    });
  };

  // Each base_value_type uses a different shape of row in `base_values`:
  //   fixed  → period_year = period_month = NULL (one row, "all periods")
  //   monthly → both fields populated (one row per (year, month))
  // The table renders saved + pending bases together, and previously kept
  // showing rows of the wrong shape when the user toggled type. Clear any
  // pending entries that don't match the new shape so they don't get
  // accidentally saved. Saved rows of the wrong shape are hidden from the
  // table below (filtered at render time); the user can switch back at any
  // point to recover them without re-typing.
  const handleBaseValueTypeChange = (newType: BaseValueType) => {
    if (newType === baseValueType) return;
    setBaseValueType(newType);
    setPendingBaseValues((prev) =>
      prev.filter((v) => {
        const isFixedShape = v.period_year === null && v.period_month === null;
        if (newType === 'fixed') return isFixedShape;
        if (newType === 'monthly') return !isFixedShape;
        // rolling_average uses neither set of pending rows
        return false;
      })
    );
  };

  // Helper function to add base value
  const handleAddBaseValue = () => {
    if (!baseValueInput.trim()) return;
    const numValue = parseFloat(baseValueInput);
    if (isNaN(numValue)) return;

    // Deviation is computed as a percentage of the base (score / base * 100).
    // A base of 0 is undefined and a negative base inverts the threshold
    // bands, so neither can produce a valid assurance level — block both at
    // entry rather than silently storing a row that will render as "No
    // assurance level data" downstream.
    if (numValue <= 0) {
      toast.error(
        'Base value must be greater than 0. Deviation is calculated as a percentage of the base, so 0 or negative values cannot produce an assurance level.'
      );
      return;
    }

    // Monthly base values need a specific (year, month). The dropdowns show
    // today's year/month as their displayed default, but the underlying state
    // stays null until the user touches them. Backfill from today so the
    // saved row matches what the dropdowns appear to show — otherwise a
    // null/null pair would be persisted as a Fixed base.
    const now = new Date();
    const effectiveYear = baseValueType === 'fixed' ? null : (baseValueYear ?? now.getFullYear());
    const effectiveMonth = baseValueType === 'fixed' ? null : (baseValueMonth ?? now.getMonth() + 1);

    const newBaseValue = {
      period_year: effectiveYear,
      period_month: effectiveMonth,
      value: numValue
    };

    // Check if this period already exists in pending
    const existsInPending = pendingBaseValues.some(
      (v) => v.period_year === newBaseValue.period_year && v.period_month === newBaseValue.period_month
    );

    if (existsInPending) {
      setPendingBaseValues((prev) =>
        prev.map((v) =>
          v.period_year === newBaseValue.period_year && v.period_month === newBaseValue.period_month
            ? { ...v, value: numValue }
            : v
        )
      );
    } else {
      setPendingBaseValues((prev) => [...prev, newBaseValue]);
    }

    setBaseValueInput('');
  };

  const handleRemoveBaseValue = (id: string, isPending: boolean) => {
    if (isPending) {
      setPendingBaseValues((prev) => prev.filter((v) => `pending-${v.period_year}-${v.period_month}` !== id));
    } else {
      setDeletedBaseValueIds((prev) => [...prev, id]);
    }
  };

  // The single base the rule summary can quote a worked example against.
  // Per-period and rolling bases differ by period, so they have no one figure
  // and the summary stays in percentage terms instead.
  const fixedBaseValue = useMemo(() => {
    if (baseValueType !== 'fixed') {
      return null;
    }
    const isFixedRow = (year: number | null, month: number | null) => year === null && month === null;
    const pending = pendingBaseValues.find((v) => isFixedRow(v.period_year, v.period_month));
    if (pending) {
      return pending.value;
    }
    const saved = (baseValues?.base_values || []).find(
      (v) => isFixedRow(v.period_year, v.period_month) && !deletedBaseValueIds.includes(v.id)
    );
    return saved ? saved.value : null;
  }, [baseValueType, baseValues, deletedBaseValueIds, pendingBaseValues]);

  // Periods that have a value but no base row to score it against. A monthly
  // base is strict on the backend — a missing row yields no base, and the
  // period comes back NaN rather than falling through to None — so a gap is
  // invisible until someone notices a hole on the board.
  //
  // Weekly cards are skipped: they key their bases by ISO week, which the
  // frontend BaseValue type doesn't carry, and a false warning is worse than
  // none.
  const periodsMissingBase = useMemo(() => {
    if (selectedMode === 'absolute' || baseValueType !== 'monthly' || updateCadence === 'weekly') {
      return [];
    }
    const covered = new Set<string>();
    for (const base of baseValues?.base_values || []) {
      if (base.period_year !== null && base.period_month !== null && !deletedBaseValueIds.includes(base.id)) {
        covered.add(`${base.period_year}-${base.period_month}`);
      }
    }
    for (const pending of pendingBaseValues) {
      if (pending.period_year !== null && pending.period_month !== null) {
        covered.add(`${pending.period_year}-${pending.period_month}`);
      }
    }

    const missing = new Map<string, { year: number; month: number }>();
    const deletedIds = new Set(deletedSavedValueIds);
    for (const value of dataValues?.data_values || []) {
      if (value.period_month === null) continue;
      if (deletedIds.has(value.id)) continue;
      const key = `${value.period_year}-${value.period_month}`;
      if (!covered.has(key)) {
        missing.set(key, { year: value.period_year, month: value.period_month });
      }
    }
    for (const pending of pendingDataValues) {
      if (pending.period_month === null) continue;
      const key = `${pending.period_year}-${pending.period_month}`;
      // A pending null is a clear of that period (saved or new). It must not
      // count as "has a value", and it must undo a saved row already added.
      if (pending.value === null) {
        missing.delete(key);
        continue;
      }
      if (!covered.has(key)) {
        missing.set(key, { year: pending.period_year, month: pending.period_month });
      }
    }
    return [...missing.values()]
      .sort((a, b) => a.year - b.year || a.month - b.month)
      .map(({ year, month }) => `${MONTHS[month - 1]} ${year}`);
  }, [
    baseValueType,
    baseValues,
    dataValues,
    deletedBaseValueIds,
    deletedSavedValueIds,
    pendingBaseValues,
    pendingDataValues,
    selectedMode,
    updateCadence
  ]);

  let rulesContent: ReactNode;

  if (useChartTiles) {
    // The tab is a sequence, not a pile of settings: the mode decides whether a
    // base value is needed at all, and what units the bands are then in. Which
    // steps exist depends on the mode — absolute needs no base, deviation_bands
    // has no direction to pick — so the numbers are derived from the steps this
    // card actually has rather than written into the labels.
    const ruleSteps: { title: string; content: React.ReactNode }[] = [];

    ruleSteps.push({
      title: 'How is this card scored?',
      content: <CalculationModePicker value={selectedMode} onChange={handleModeChange} disabled={structuralReadOnly} />
    });

    if (selectedMode !== 'absolute') {
      ruleSteps.push({
        title: 'Set the base value',
        content: (
          <div className="space-y-4">
            {/* Base Value Type */}
            <div className="flex flex-col gap-2">
              <SegmentedControl
                ariaLabel="How to determine base value"
                value={baseValueType}
                onChange={handleBaseValueTypeChange}
                disabled={structuralReadOnly}
                options={BASE_VALUE_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                  tooltip: o.description
                }))}
              />
              <p className="text-xs text-content-subtle">
                {BASE_VALUE_TYPE_OPTIONS.find((o) => o.value === baseValueType)?.description}
              </p>
            </div>

            {/* A period with no base value scores as No data, not None — the
              backend is strict about a missing monthly base (see
              _get_base_value_from_context), so a gap silently leaves a hole on
              the board. Surface it here, next to the rows that cause it. */}
            {baseValueType === 'monthly' && periodsMissingBase.length > 0 && (
              <Banner variant="warning" icon={WarningIcon}>
                <span className="text-sm">
                  <strong className="font-semibold">
                    {periodsMissingBase.length} {periodsMissingBase.length === 1 ? 'period has' : 'periods have'} a
                    value but no base
                  </strong>{' '}
                  ({periodsMissingBase.slice(0, 3).join(', ')}
                  {periodsMissingBase.length > 3 ? `, +${periodsMissingBase.length - 3} more` : ''}). Those periods
                  score as <em>No data</em> rather than None. The card shows a gap instead of a level.
                </span>
              </Banner>
            )}

            {/* Rolling Average Months */}
            {baseValueType === 'rolling_average' && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-content-subtle">Rolling Average Period (months)</label>
                <InputField
                  type="text"
                  value={rollingAverageMonths === null ? '' : String(rollingAverageMonths)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setRollingAverageMonths(null);
                      return;
                    }
                    const parsed = parseInt(raw, 10);
                    setRollingAverageMonths(Number.isNaN(parsed) ? null : parsed);
                  }}
                  min={1}
                  max={24}
                  placeholder="1 to 24"
                  disabled={structuralReadOnly}
                  state={
                    rollingAverageMonths !== null && (rollingAverageMonths < 1 || rollingAverageMonths > 24)
                      ? 'error'
                      : 'default'
                  }
                />
                {rollingAverageMonths === null && (
                  <p className="text-xs text-red-600">Choose a value between 1 and 24 months.</p>
                )}
                {rollingAverageMonths !== null && (rollingAverageMonths < 1 || rollingAverageMonths > 24) && (
                  <p className="text-xs text-red-600">Must be between 1 and 24 months.</p>
                )}
              </div>
            )}

            {/* Base Value Input (for fixed or monthly) */}
            {baseValueType !== 'rolling_average' && (
              <div className="border-t border-content-subtle/20 pt-4">
                <label className="text-sm font-semibold text-content-subtle mb-2 block">
                  {baseValueType === 'fixed' ? 'Fixed Base Value' : 'Monthly Base Values'}
                </label>

                <div className={`grid gap-4 items-end ${baseValueType === 'monthly' ? 'grid-cols-4' : 'grid-cols-2'}`}>
                  {baseValueType === 'monthly' && (
                    <>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs text-content-subtle">Year</label>
                        <Dropdown
                          options={[2024, 2025, 2026].map((y) => ({ value: String(y), label: String(y) }))}
                          initialValue={String(baseValueYear || new Date().getFullYear())}
                          onChange={(v) => setBaseValueYear(parseInt(v as string))}
                          width="w-full"
                          disabled={structuralReadOnly}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs text-content-subtle">Month</label>
                        <Dropdown
                          options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
                          initialValue={String(baseValueMonth || new Date().getMonth() + 1)}
                          onChange={(v) => setBaseValueMonth(parseInt(v as string))}
                          width="w-full"
                          disabled={structuralReadOnly}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-content-subtle">Base Value</label>
                    <div className="flex gap-2">
                      <InputField
                        value={baseValueInput}
                        onChange={(e) => setBaseValueInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && baseValueInput.trim()) {
                            e.preventDefault();
                            handleAddBaseValue();
                          }
                        }}
                        placeholder="e.g., 100000"
                        fullWidth
                        type="text"
                        disabled={structuralReadOnly}
                      />
                      <Button
                        variant="primary"
                        icon={PlusIcon}
                        onClick={handleAddBaseValue}
                        disabled={structuralReadOnly || !baseValueInput.trim()}
                        aria-label="Add base value"
                      />
                    </div>
                  </div>
                </div>

                {/* Display existing and pending base values matching the
                  currently-selected type. Rows of the wrong shape (e.g. a
                  Fixed row left over after switching to Monthly) are hidden
                  rather than deleted, so toggling back recovers them. */}
                {(() => {
                  const matchesCurrentType = (year: number | null, month: number | null) => {
                    const isFixedShape = year === null && month === null;
                    if (baseValueType === 'fixed') return isFixedShape;
                    if (baseValueType === 'monthly') return !isFixedShape;
                    return false; // rolling_average uses no per-row bases
                  };

                  const savedBaseVals = (baseValues?.base_values || [])
                    .filter((v) => !deletedBaseValueIds.includes(v.id))
                    .filter((v) => matchesCurrentType(v.period_year, v.period_month))
                    .map((v) => ({
                      id: v.id,
                      period_year: v.period_year,
                      period_month: v.period_month,
                      value: v.value,
                      isPending: false
                    }));

                  const pendingWithIds = pendingBaseValues
                    .filter((v) => matchesCurrentType(v.period_year, v.period_month))
                    .map((v) => ({
                      id: `pending-${v.period_year}-${v.period_month}`,
                      period_year: v.period_year,
                      period_month: v.period_month,
                      value: v.value,
                      isPending: true
                    }));

                  const filteredSaved = savedBaseVals.filter(
                    (sv) =>
                      !pendingBaseValues.some(
                        (pv) => pv.period_year === sv.period_year && pv.period_month === sv.period_month
                      )
                  );

                  const combinedBaseValues = [...pendingWithIds, ...filteredSaved];

                  if (combinedBaseValues.length === 0) return null;

                  return (
                    <div className="mt-4">
                      <DataTable
                        data={combinedBaseValues}
                        columns={[
                          {
                            key: 'period',
                            header: 'Period',
                            render: (item) =>
                              item.period_year === null
                                ? 'Fixed (all periods)'
                                : `${MONTHS[item.period_month! - 1]} ${item.period_year}`
                          },
                          {
                            key: 'value',
                            header: 'Base Value',
                            align: 'right',
                            render: (item) => String(item.value)
                          }
                        ]}
                        actions={
                          structuralReadOnly
                            ? []
                            : [
                                {
                                  label: 'Remove',
                                  icon: TrashIcon,
                                  onClick: (item) => handleRemoveBaseValue(item.id, item.isPending)
                                }
                              ]
                        }
                        itemsPerPage={5}
                        enableSorting={false}
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )
      });
    }

    // Two options, always both visible — a dropdown hid half the answer behind a
    // click, and which way is "good" is the thing people most often get wrong on
    // a new card. deviation_bands is omitted because the backend pins it
    // ascending whatever the card stores.
    if (selectedMode === 'absolute' || selectedMode === 'deviation_direct') {
      ruleSteps.push({
        title: 'Which way is good?',
        content: (
          <div className="flex flex-col gap-2">
            <SegmentedControl
              ariaLabel="Which way is good?"
              value={boundaryDirection}
              onChange={handleDirectionChange}
              disabled={structuralReadOnly}
              options={BOUNDARY_DIRECTION_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                tooltip: o.description
              }))}
            />
            <p className="text-xs text-content-subtle">
              {BOUNDARY_DIRECTION_OPTIONS.find((o) => o.value === boundaryDirection)?.description}
            </p>
          </div>
        )
      });
    }

    ruleSteps.push({
      title:
        selectedMode === 'absolute'
          ? 'Set the bands'
          : selectedMode === 'deviation_bands'
            ? 'Set the bands, as ± distance from base'
            : 'Set the bands, as % of base',
      content: (
        <div>
          {/* Typing can no longer produce an out-of-order set — the bands nudge
            each other instead — so this now only reports thresholds that
            arrived from elsewhere: AI-populated cards, imports, older data. */}
          {thresholdErrors.length > 0 && (
            <Banner variant="error" icon={WarningIcon} className="mb-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">Threshold validation error</span>
                {thresholdErrors.some((e) => e.code === 'unordered_thresholds') && (
                  <span className="text-xs">{getThresholdOrderDescription(boundaryDirection, selectedMode)}</span>
                )}
                <ul className="list-inside list-disc text-xs">
                  {thresholdErrors.map((error, idx) => (
                    <li key={idx}>{error.message}</li>
                  ))}
                </ul>
              </div>
            </Banner>
          )}

          {/* The bottom level is the catch-all — anything that didn't qualify for
            one of the four bands above lands there, which is why it is drawn as
            a band but never gets a field. Its saved threshold is always null;
            the calc treats null as catch-all, see
            calcard_repositories._get_assurance_level_from_context. */}
          <ThresholdBandBar
            thresholds={levelBoundaries}
            onChange={(next) => {
              setThresholdsUserEditedSession(true);
              setLevelBoundaries(next);
            }}
            mode={selectedMode}
            direction={boundaryDirection}
            dataValueType={dataValueType}
            disabled={structuralReadOnly}
            errorLevels={thresholdErrors
              .map((e) => e.field.replace('threshold_', '') as Level)
              .filter((level) => LEVEL_OPTIONS.some((opt) => opt.value === level))}
          />

          {/* Replaces the worked example that used to quote £100,000 at every
            card — this one reads back the rule actually configured, against
            this card's own base where there is a single one. */}
          <div className="mt-4">
            <AssuranceRuleSummary
              thresholds={levelBoundaries}
              mode={selectedMode}
              direction={boundaryDirection}
              dataValueType={dataValueType}
              baseValueType={baseValueType}
              fixedBaseValue={fixedBaseValue}
              rollingAverageMonths={rollingAverageMonths}
            />
          </div>
        </div>
      )
    });

    rulesContent = (
      // `word-break: normal` undoes the global `ol { word-break: break-all }` in
      // index.css, which is there for pasted markdown and would otherwise split
      // these labels mid-word at panel width ("equally of / f target").
      <ol className="flex flex-col [word-break:normal]">
        {ruleSteps.map((step, index) => (
          <RuleStep key={step.title} number={index + 1} title={step.title} isLast={index === ruleSteps.length - 1}>
            {step.content}
          </RuleStep>
        ))}
      </ol>
    );
  } else {
    const catchAllNote = ' Anything that doesn’t reach Limited is None.';
    const thresholdDescription =
      selectedMode === 'absolute'
        ? (boundaryDirection === 'descending'
            ? 'A value must be ≥ the threshold to achieve that level.'
            : 'A value must be ≤ the threshold to achieve that level.') + catchAllNote
        : selectedMode === 'deviation_bands'
          ? 'Thresholds represent deviation bands (e.g., 3 means within ±3% of base). Closer to base = better.' +
            catchAllNote
          : (boundaryDirection === 'descending'
              ? 'Thresholds are % of base (e.g., 150 means 150% of base). Higher % = better.'
              : 'Thresholds are % of base (e.g., 50 means 50% of base). Lower % = better.') + catchAllNote;
    const thresholdColumnHeader =
      selectedMode === 'absolute'
        ? 'Threshold'
        : selectedMode === 'deviation_bands'
          ? 'Max Deviation (%)'
          : '% of Base';
    const thresholdPlaceholder =
      selectedMode === 'absolute'
        ? 'e.g., 90'
        : selectedMode === 'deviation_bands'
          ? 'e.g., 3 for ±3%'
          : 'e.g., 150 for 150% of base';
    const catchAllLabel =
      selectedMode === 'absolute'
        ? boundaryDirection === 'descending'
          ? 'Everything below Limited'
          : 'Everything above Limited'
        : selectedMode === 'deviation_bands'
          ? 'Anything past the Limited band'
          : boundaryDirection === 'descending'
            ? 'Everything below the Limited %'
            : 'Everything above the Limited %';
    const interpretedAs = (thresholdValue: string) => {
      const val = thresholdValue || '0';
      if (selectedMode === 'absolute') {
        return boundaryDirection === 'descending' ? `≥ ${val}` : `≤ ${val}`;
      }
      if (selectedMode === 'deviation_bands') {
        return `±${val}%`;
      }
      return boundaryDirection === 'descending' ? `≥ ${val}%` : `≤ ${val}%`;
    };

    rulesContent = (
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-content-subtle">Calculation Mode</label>
          <Dropdown
            options={CALCULATION_MODE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            initialValue={selectedMode}
            onChange={(v) => handleModeChange(v as LevelCalculationMode)}
            width="w-full"
            disabled={structuralReadOnly}
          />
          <p className="text-xs text-content-subtle">
            {CALCULATION_MODE_OPTIONS.find((o) => o.value === selectedMode)?.description}
          </p>
          <p className="text-xs text-blue-600 italic">
            {CALCULATION_MODE_OPTIONS.find((o) => o.value === selectedMode)?.example}
          </p>
        </div>

        {selectedMode !== 'absolute' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
            <h4 className="text-sm font-semibold text-blue-900">Base Value Configuration</h4>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-content-subtle">How to determine base value</label>
              <Dropdown
                options={BASE_VALUE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                initialValue={baseValueType}
                onChange={(v) => handleBaseValueTypeChange(v as BaseValueType)}
                width="w-full"
                disabled={structuralReadOnly}
              />
              <p className="text-xs text-content-subtle">
                {BASE_VALUE_TYPE_OPTIONS.find((o) => o.value === baseValueType)?.description}
              </p>
            </div>

            {baseValueType === 'rolling_average' && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-content-subtle">Rolling Average Period (months)</label>
                <InputField
                  type="text"
                  value={rollingAverageMonths === null ? '' : String(rollingAverageMonths)}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setRollingAverageMonths(null);
                      return;
                    }
                    const parsed = parseInt(raw, 10);
                    setRollingAverageMonths(Number.isNaN(parsed) ? null : parsed);
                  }}
                  min={1}
                  max={24}
                  placeholder="1–24"
                  disabled={structuralReadOnly}
                  state={
                    rollingAverageMonths !== null && (rollingAverageMonths < 1 || rollingAverageMonths > 24)
                      ? 'error'
                      : 'default'
                  }
                />
                {rollingAverageMonths === null && (
                  <p className="text-xs text-red-600">Choose a value between 1 and 24 months.</p>
                )}
                {rollingAverageMonths !== null && (rollingAverageMonths < 1 || rollingAverageMonths > 24) && (
                  <p className="text-xs text-red-600">Must be between 1 and 24 months.</p>
                )}
              </div>
            )}

            {baseValueType !== 'rolling_average' && (
              <div className="border-t border-blue-200 pt-4">
                <label className="text-sm font-semibold text-content-subtle mb-2 block">
                  {baseValueType === 'fixed' ? 'Fixed Base Value' : 'Monthly Base Values'}
                </label>

                <div className={`grid gap-4 items-end ${baseValueType === 'monthly' ? 'grid-cols-4' : 'grid-cols-2'}`}>
                  {baseValueType === 'monthly' && (
                    <>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs text-content-subtle">Year</label>
                        <Dropdown
                          options={[2024, 2025, 2026].map((y) => ({ value: String(y), label: String(y) }))}
                          initialValue={String(baseValueYear || new Date().getFullYear())}
                          onChange={(v) => setBaseValueYear(parseInt(v as string))}
                          width="w-full"
                          disabled={structuralReadOnly}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs text-content-subtle">Month</label>
                        <Dropdown
                          options={MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))}
                          initialValue={String(baseValueMonth || new Date().getMonth() + 1)}
                          onChange={(v) => setBaseValueMonth(parseInt(v as string))}
                          width="w-full"
                          disabled={structuralReadOnly}
                        />
                      </div>
                    </>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="text-xs text-content-subtle">Base Value</label>
                    <div className="flex gap-2">
                      <InputField
                        value={baseValueInput}
                        onChange={(e) => setBaseValueInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && baseValueInput.trim()) {
                            e.preventDefault();
                            handleAddBaseValue();
                          }
                        }}
                        placeholder="e.g., 100000"
                        fullWidth
                        type="text"
                        disabled={structuralReadOnly}
                      />
                      <Button
                        variant="primary"
                        icon={PlusIcon}
                        onClick={handleAddBaseValue}
                        disabled={structuralReadOnly || !baseValueInput.trim()}
                        aria-label="Add base value"
                      />
                    </div>
                  </div>
                </div>

                {(() => {
                  const matchesCurrentType = (year: number | null, month: number | null) => {
                    const isFixedShape = year === null && month === null;
                    if (baseValueType === 'fixed') return isFixedShape;
                    if (baseValueType === 'monthly') return !isFixedShape;
                    return false;
                  };

                  const savedBaseVals = (baseValues?.base_values || [])
                    .filter((v) => !deletedBaseValueIds.includes(v.id))
                    .filter((v) => matchesCurrentType(v.period_year, v.period_month))
                    .map((v) => ({
                      id: v.id,
                      period_year: v.period_year,
                      period_month: v.period_month,
                      value: v.value,
                      isPending: false
                    }));

                  const pendingWithIds = pendingBaseValues
                    .filter((v) => matchesCurrentType(v.period_year, v.period_month))
                    .map((v) => ({
                      id: `pending-${v.period_year}-${v.period_month}`,
                      period_year: v.period_year,
                      period_month: v.period_month,
                      value: v.value,
                      isPending: true
                    }));

                  const filteredSaved = savedBaseVals.filter(
                    (sv) =>
                      !pendingBaseValues.some(
                        (pv) => pv.period_year === sv.period_year && pv.period_month === sv.period_month
                      )
                  );

                  const combinedBaseValues = [...pendingWithIds, ...filteredSaved];

                  if (combinedBaseValues.length === 0) return null;

                  return (
                    <div className="mt-4">
                      <DataTable
                        data={combinedBaseValues}
                        columns={[
                          {
                            key: 'period',
                            header: 'Period',
                            render: (item) =>
                              item.period_year === null
                                ? 'Fixed (all periods)'
                                : `${MONTHS[item.period_month! - 1]} ${item.period_year}`
                          },
                          {
                            key: 'value',
                            header: 'Base Value',
                            align: 'right',
                            render: (item) => String(item.value)
                          }
                        ]}
                        actions={
                          structuralReadOnly
                            ? []
                            : [
                                {
                                  label: 'Remove',
                                  icon: TrashIcon,
                                  onClick: (item) => handleRemoveBaseValue(item.id, item.isPending)
                                }
                              ]
                        }
                        itemsPerPage={5}
                        enableSorting={false}
                      />
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {(selectedMode === 'absolute' || selectedMode === 'deviation_direct') && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold text-content-subtle">Scoring Direction</label>
            <Dropdown
              options={BOUNDARY_DIRECTION_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              initialValue={boundaryDirection}
              onChange={(v) => handleDirectionChange(v as BoundaryDirection)}
              width="w-full"
              disabled={structuralReadOnly}
            />
            <p className="text-xs text-content-subtle">
              {BOUNDARY_DIRECTION_OPTIONS.find((o) => o.value === boundaryDirection)?.description}
            </p>
          </div>
        )}

        <div className="border-t border-content-subtle/20 pt-4 threshold-inputs">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-content-subtle">{thresholdColumnHeader}</span>
            <span className="text-xs text-content-subtle">Interpreted As</span>
          </div>
          <p className="text-sm text-content-subtle mb-3">{thresholdDescription}</p>

          {thresholdErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <WarningIcon size={20} className="text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Threshold validation error</p>
                  {thresholdErrors.some((e) => e.code === 'unordered_thresholds') && (
                    <p className="text-xs text-red-700 mt-1">
                      {getThresholdOrderDescription(boundaryDirection, selectedMode)}
                    </p>
                  )}
                  <ul className="text-xs text-red-700 mt-2 list-disc list-inside">
                    {thresholdErrors.map((error, idx) => (
                      <li key={idx}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {LEVEL_OPTIONS.map((opt) => {
              const hasError = thresholdErrors.some((e) => e.field === `threshold_${opt.value}`);
              const isCatchAll = opt.value === 'none';
              return (
                <div key={opt.value} className="flex items-center gap-4">
                  <div className="w-28 text-sm font-semibold text-content-subtle">{opt.label}</div>
                  <div className="flex-1">
                    {isCatchAll ? (
                      <div className="px-3 py-2 text-sm text-content-subtle italic rounded">{catchAllLabel}</div>
                    ) : (
                      <InputField
                        value={levelBoundaries[opt.value]}
                        onChange={(e) => {
                          setThresholdsUserEditedSession(true);
                          setLevelBoundaries((prev) => ({
                            ...prev,
                            [opt.value]: e.target.value
                          }));
                        }}
                        placeholder={thresholdPlaceholder}
                        type="text"
                        state={hasError ? 'error' : 'default'}
                        disabled={structuralReadOnly}
                      />
                    )}
                  </div>
                  <div
                    className={`text-xs w-28 text-right font-mono ${hasError ? 'text-red-600' : 'text-content-subtle'}`}
                  >
                    {isCatchAll ? '' : interpretedAs(levelBoundaries[opt.value])}
                  </div>
                </div>
              );
            })}
          </div>

          {selectedMode !== 'absolute' && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-content-subtle">
              <strong>Example:</strong>{' '}
              {selectedMode === 'deviation_bands' ? (
                <>
                  If base = £100,000 and actual = £103,000, that&apos;s <span className="font-mono">+3%</span>{' '}
                  deviation. With threshold <span className="font-mono">3</span>, this achieves the level (within ±3% of
                  base).
                </>
              ) : (
                <>
                  If base = 100 and actual = 150, that&apos;s <span className="font-mono">150%</span> of base. Compare
                  this against your thresholds (e.g., ≥150% → High for sales targets, or ≤50% → High for failure
                  reduction).
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Linked dashboards tab content
  const linkedDashboardsContent = (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <LinkIcon size={20} className="text-secondary-blue" />
        <p className="text-sm text-content-subtle">
          This card is linked in the following dashboards. Changes to this card will be reflected there.
        </p>
      </div>

      {linkedDashboardsLoading ? (
        <Loading text="Loading linked dashboards..." />
      ) : linkedDashboards?.dashboards && linkedDashboards.dashboards.length > 0 ? (
        <div className="space-y-2">
          {linkedDashboards.dashboards.map((dashboard) => (
            <div
              key={`${dashboard.dashboard_id}-${dashboard.linked_card_id}`}
              className="flex items-center justify-between p-3 border border-border-default rounded-lg hover:bg-gray-50"
            >
              <div className="flex flex-col">
                <span className="font-medium text-sm">{dashboard.dashboard_name}</span>
                <span className="text-xs text-content-subtle">Linked as: "{dashboard.linked_card_name}"</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                icon={ArrowSquareOutIcon}
                onClick={() => {
                  posthog?.capture('assurance_linked_dashboard_navigate', {
                    source_node_id: nodeId,
                    target_dashboard_id: dashboard.dashboard_id
                  });
                  onClose();
                  navigate(`/reporting/dashboard/${dashboard.dashboard_id}?openNode=${dashboard.linked_card_id}`);
                }}
                title="Open dashboard"
              >
                Open
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-content-subtle">
          <LinkIcon size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">This card is not linked in any other dashboards.</p>
        </div>
      )}
    </div>
  );

  // ==================== Activity Tab ====================

  const formatFieldLabel = (key: string) => {
    const labels: Record<string, string> = {
      name: 'Name',
      data_value_type: 'Data type',
      aggregation_type: 'Aggregation',
      boundary_direction: 'Direction',
      level_calculation_mode: 'Calculation mode',
      base_value_type: 'Base value type',
      update_cadence: 'Update cadence',
      value: 'Value',
      period_year: 'Year',
      period_month: 'Month',
      level: 'Level',
      threshold_value: 'Threshold'
    };
    return labels[key] || snakeToTitleCase(key);
  };

  const formatAuditDataValue = (value: unknown): string => {
    if (isStatusCard) {
      if (value === 1 || value === 1.0) return 'Yes';
      if (value === 0 || value === 0.0) return 'No';
    }
    return String(value);
  };

  const formatAuditPeriod = (row: Record<string, unknown> | null | undefined) => {
    if (!row) return '';
    const year = typeof row.period_year === 'number' ? row.period_year : undefined;
    const month = typeof row.period_month === 'number' ? row.period_month : undefined;
    const week = typeof row.period_week === 'number' ? row.period_week : undefined;
    if (year == null) return '';
    if (week != null) return formatPeriodLabel(year, month ?? 0, 'weekly', week);
    if (month == null) return '';
    return formatPeriodLabel(year, month, updateCadence);
  };

  const auditName = (text: string) => <span className="font-medium text-content-default">{text}</span>;
  const auditVal = (text: string) => <span className="italic text-content-default">{text}</span>;
  const auditActor = (userId: number | null) =>
    user?.user_id && userId === user.user_id ? 'You' : getUserDisplayName(userId);

  const describeAuditLog = (log: NodeAuditLogEntry): { icon: Icon; body: ReactNode } | null => {
    const actor = auditActor(log.user_id);

    if (log.is_create) {
      if (log.change_type === 'data_value' && log.new_value) {
        const period = formatAuditPeriod(log.new_value);
        return {
          icon: PlusIcon,
          body: (
            <>
              {auditName(actor)} added value {auditVal(formatAuditDataValue(log.new_value.value))}
              {period ? <> for {auditVal(period)}</> : null}
            </>
          )
        };
      }
      if (log.change_type === 'boundary' && log.new_value) {
        return {
          icon: CheckIcon,
          body: (
            <>
              {auditName(actor)} set {auditVal(String(log.new_value.level ?? ''))} threshold to{' '}
              {auditVal(String(log.new_value.threshold_value))}
            </>
          )
        };
      }
      return {
        icon: PlusIcon,
        body: <>{auditName(actor)} created this card</>
      };
    }

    if (log.change_type === 'data_value') {
      const period = formatAuditPeriod(log.new_value) || formatAuditPeriod(log.old_value);
      if (log.old_value && !log.new_value) {
        return {
          icon: TrashIcon,
          body: (
            <>
              {auditName(actor)} deleted value {auditVal(formatAuditDataValue(log.old_value.value))}
              {period ? <> for {auditVal(period)}</> : null}
            </>
          )
        };
      }
      if (log.old_value?.value !== undefined && log.new_value?.value !== undefined) {
        return {
          icon: CheckIcon,
          body: (
            <>
              {auditName(actor)} changed {period ? <>the {auditVal(period)} value</> : 'the value'} to{' '}
              {auditVal(formatAuditDataValue(log.new_value.value))}
            </>
          )
        };
      }
      return null;
    }

    if (log.old_value && log.new_value) {
      const changedFields = Object.keys(log.new_value).filter(
        (key) =>
          !['period_year', 'period_month', 'period_week'].includes(key) && log.old_value![key] !== log.new_value![key]
      );
      if (changedFields.length === 0) return null;
      return {
        icon: GearIcon,
        body: (
          <>
            {auditName(actor)}{' '}
            {changedFields.map((key, index) => (
              <span key={key}>
                {index > 0 ? '; ' : null}
                changed {formatFieldLabel(key).toLowerCase()} from {auditVal(String(log.old_value![key] ?? 'none'))} to{' '}
                {auditVal(String(log.new_value![key] ?? 'none'))}
              </span>
            ))}
          </>
        )
      };
    }

    if (log.change_type === 'boundary' && log.old_value && !log.new_value) {
      return {
        icon: TrashIcon,
        body: (
          <>
            {auditName(actor)} removed the {auditVal(String(log.old_value.level ?? ''))} threshold
          </>
        )
      };
    }

    return null;
  };

  const auditItems = (auditLogData?.audit_logs ?? [])
    .map((log) => {
      const described = describeAuditLog(log);
      if (!described) return null;
      return { id: log.id, created_at: log.created_at, ...described };
    })
    .filter((item): item is NonNullable<typeof item> => item != null);

  const activityContent = auditLogsLoading ? (
    <p className="text-sm text-content-subtle">Loading activity…</p>
  ) : auditItems.length === 0 ? (
    <p className="text-sm text-content-subtle">No activity yet.</p>
  ) : (
    <ul className="flex flex-col">
      {auditItems.map((item, index) => {
        const ItemIcon = item.icon;
        return (
          <TimelineShell
            key={item.id}
            isLast={index === auditItems.length - 1}
            icon={
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border-default text-content-subtle">
                <ItemIcon size={16} weight="light" />
              </span>
            }
          >
            <div className="flex min-h-8 items-center justify-between gap-2">
              <p className="text-sm text-content-subtle">{item.body}</p>
              <span className="shrink-0 text-xs text-content-subtle lining-nums">
                {formatRelativeTime(item.created_at)}
              </span>
            </div>
          </TimelineShell>
        );
      })}
    </ul>
  );

  // Build tabs based on card type
  // Data Values first (most commonly used), then Assurance Rules, then Details
  // For data entry only users, wrap rules in a disabled container
  const rulesTabContent = dataEntryOnly ? (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
        <p className="text-sm text-amber-800">
          <strong>View only</strong>: you need Edit permission to change assurance rules.
        </p>
      </div>
      <div className="opacity-60" inert>
        {rulesContent}
      </div>
    </div>
  ) : (
    rulesContent
  );

  // The audit trail endpoint requires OWNER permission on the node, and only
  // makes sense for cards that have their own data/rules history. Group cards
  // aggregate from children, so there is nothing useful to show on them.
  const canViewAuditTrail = hasDataValues && node?.user_permission === ResourcePermissionRole.OWNER;

  const baseTabs = isDataCard
    ? [
        { label: 'Data Values', content: dataValuesContent },
        { label: 'Assurance Rules', content: rulesTabContent },
        { label: 'Details', content: detailsContent },
        { label: 'Display', content: displayContent },
        ...(canViewAuditTrail ? [{ label: 'Audit Trail', content: activityContent }] : [])
      ]
    : isStatusCard
      ? [
          { label: 'Data Values', content: dataValuesContent },
          { label: 'Details', content: detailsContent },
          { label: 'Display', content: displayContent },
          ...(canViewAuditTrail ? [{ label: 'Audit Trail', content: activityContent }] : [])
        ]
      : [
          { label: 'Details', content: detailsContent },
          { label: 'Display', content: displayContent }
        ];

  // Reminders are configured per dashboard. Nested groups and data cards can
  // opt out or take a different deadline; they still follow the dashboard's
  // reminder days. Group cards hold no data themselves — the override applies
  // to their children.
  const isDashboardRoot = !!node && !node.parent_id;
  const isOwner = node?.user_permission === ResourcePermissionRole.OWNER;
  const remindersTab = isDashboardRoot
    ? {
        label: 'Reminders',
        content: (
          <RemindersTab
            dashboardId={nodeId}
            canEdit={isOwner}
            onDirtyChange={handleReminderDirty}
            onRegisterEditor={handleRegisterReminderEditor}
          />
        )
      }
    : {
        label: 'Reminders',
        content: (
          <CardRemindersTab
            cardId={nodeId}
            isGroup={isGroupCard}
            canEdit={isOwner}
            onDirtyChange={handleReminderDirty}
            onRegisterEditor={handleRegisterReminderEditor}
            onOpenDashboardReminders={onOpenDashboardReminders ? handleOpenDashboardReminders : undefined}
          />
        )
      };
  const tabsWithReminders = remindersTab ? [...baseTabs, remindersTab] : baseTabs;

  // Add linked dashboards tab if there are any linked dashboards
  const hasLinkedDashboards = linkedDashboards?.dashboards && linkedDashboards.dashboards.length > 0;
  const allTabs = hasLinkedDashboards
    ? [
        ...tabsWithReminders,
        { label: `Linked (${linkedDashboards.dashboards.length})`, content: linkedDashboardsContent }
      ]
    : tabsWithReminders;
  const tabs =
    sections === 'history'
      ? allTabs.filter((t) => t.label === 'Data Values')
      : sections === 'audit'
        ? allTabs.filter((t) => t.label === 'Audit Trail')
        : sections === 'settings'
          ? allTabs.filter((t) => t.label !== 'Data Values' && t.label !== 'Audit Trail')
          : allTabs;

  remindersTabIndexRef.current = tabs.findIndex((tab) => tab.label === 'Reminders');

  const tabLabels = tabs.map((tab) => tab.label).join('|');
  useEffect(() => {
    if (!initialTabLabel) return;
    const index = tabLabels.split('|').indexOf(initialTabLabel);
    if (index >= 0) setActiveTabIndex(index);
  }, [initialTabLabel, nodeId, tabLabels]);

  const showSave = !readOnly && sections !== 'audit' && !(sections === 'history' && !hasDataValues);
  const showChromeActions =
    !structuralReadOnly && !hideChromeActions && (sections === 'full' || sections === 'settings');
  const embedded = chrome === 'none';

  const mainContent = isLoading ? (
    <Loading />
  ) : sections === 'history' ? (
    hasDataValues ? (
      dataValuesContent
    ) : (
      <p className="text-sm text-content-subtle">Group cards don’t have period values.</p>
    )
  ) : sections === 'audit' ? (
    canViewAuditTrail ? (
      activityContent
    ) : (
      <p className="text-sm text-content-subtle">Only the card owner can view activity.</p>
    )
  ) : (
    <Tabs
      tabs={tabs}
      activeIndexExternal={activeTabIndex}
      onTabChange={(index) => {
        setActiveTabIndex(index);
        if (tabs[index]?.label === 'Audit Trail') {
          setAuditTrailTabVisited(true);
          posthog?.capture('assurance_audit_trail_viewed', {
            dashboard_id: scopeId,
            node_id: nodeId,
            node_type: node?.type
          });
        }
      }}
      contentMargin={true}
    />
  );

  const pinActionsToPortal = embedded && actionsPortal != null;
  const actionsBar =
    showSave || showChromeActions ? (
      <div
        className={`flex justify-end items-center gap-2 ${
          pinActionsToPortal ? '' : 'w-full border-t border-border-default'
        } ${embedded && !pinActionsToPortal ? 'pt-3 shrink-0' : pinActionsToPortal ? '' : 'pt-4'}`}
      >
        {showChromeActions && (
          <Button variant="danger" icon={TrashIcon} onClick={() => setShowDeleteConfirm(true)}>
            Delete
          </Button>
        )}
        {showChromeActions && (
          <Button
            variant="outline"
            icon={CopyIcon}
            onClick={handleDuplicate}
            disabled={isDuplicating}
            loading={isDuplicating}
            title="Duplicate this card and all children (linked cards will keep their links)"
          >
            Duplicate
          </Button>
        )}
        {showSave && (
          <Button
            variant="primary"
            icon={FloppyDiskIcon}
            onClick={() => handleSaveAll()}
            disabled={isSaving || !nodeName.trim()}
            loading={isSaving}
            className="save-details-button"
          >
            Save
          </Button>
        )}
      </div>
    ) : null;

  const formBody = embedded ? (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div
        className={`flex-1 min-h-0 ${
          sections === 'history' ? 'overflow-hidden flex flex-col h-full' : 'overflow-y-auto'
        }`}
      >
        {mainContent}
      </div>
      {!pinActionsToPortal && actionsBar}
    </div>
  ) : (
    <>
      {mainContent}
      {actionsBar}
    </>
  );

  return (
    <>
      {pinActionsToPortal && actionsBar && actionsPortal ? createPortal(actionsBar, actionsPortal) : null}
      {chrome === 'modal' ? (
        <Modal
          open={isOpen}
          onClose={handleClose}
          title={<h2 className="text-base font-medium text-content-default">{node?.title || 'Settings'}</h2>}
          className="w-full !max-w-2xl p-6"
          disableBackdropClick
        >
          {formBody}
        </Modal>
      ) : isOpen ? (
        formBody
      ) : null}

      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Card"
        message={(() => {
          const dataValueCount = dataValues?.data_values?.length ?? 0;
          const linkedCount = linkedDashboards?.dashboards?.length ?? 0;
          // Cross-dashboard impact: the server cascade-removes any linked
          // copies of this card on other dashboards (service.delete_metric_node),
          // so warn the deleter explicitly. linkedDashboards is already
          // fetched on modal open via useLinkedDashboards.
          const linkedSuffix =
            linkedCount > 0
              ? ` This card is also linked from ${linkedCount} other dashboard${
                  linkedCount === 1 ? '' : 's'
                }. Those linked copies will be removed too.`
              : '';
          if (isGroupCard) {
            return (
              `Are you sure you want to delete "${node?.title ?? 'this card'}"? ` +
              `This will permanently remove the group AND every child card, sub-group, ` +
              `data value, threshold and comment beneath it. This cascade cannot be undone.${linkedSuffix}`
            );
          }
          if (dataValueCount > 0) {
            return (
              `Deleting "${node?.title ?? 'this card'}" will also remove ${dataValueCount} ` +
              `data value${dataValueCount === 1 ? '' : 's'}, plus any thresholds, base ` +
              `values and comments on it. This action cannot be undone.${linkedSuffix}`
            );
          }
          return `Are you sure you want to delete "${node?.title ?? 'this card'}"? This action cannot be undone.${linkedSuffix}`;
        })()}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      <Modal
        open={!!pendingTypeChange}
        onClose={() => {
          setPendingTypeChange(null);
          setIsSaving(false);
        }}
        title="Existing data values may not match the new configuration"
      >
        <div className="space-y-4">
          {pendingTypeChange && (
            <>
              <p className="text-sm text-content-default">
                You're changing the <strong>{pendingTypeChange.fields.join(', ')}</strong> on a card that already has{' '}
                <strong>
                  {pendingTypeChange.valueCount} saved data value{pendingTypeChange.valueCount === 1 ? '' : 's'}
                </strong>
                . Existing values are not automatically recalculated against the new configuration.
              </p>
              <p className="text-sm text-content-subtle">Choose how to proceed:</p>
              <ul className="text-xs text-content-subtle list-disc list-inside space-y-1">
                <li>
                  <strong>Keep values</strong>: values stay as they are; they may show as invalid until you review them.
                  {pendingTypeChange.fields.includes('update cadence') && node?.update_cadence === 'monthly' && (
                    <>
                      {' '}
                      For this cadence switch we'll also snap every saved monthly row to its quarter-end month so it
                      stays visible (multiple values in the same quarter collapse to the most recent).
                    </>
                  )}
                </li>
                <li>
                  <strong>Delete values</strong>: all existing data values for this card are removed. Cannot be undone.
                </li>
              </ul>
            </>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setPendingTypeChange(null);
                setIsSaving(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                // Stage all currently saved data values for deletion alongside the
                // config change. Pass 'wipe' as an explicit override so the
                // re-entry into handleSaveAll skips the prompt; relying on a
                // freshly-set `typeChangeChoice` state value would miss the
                // closure (and previously caused the prompt to fire twice).
                const ids = (dataValues?.data_values || []).map((v) => v.id);
                setDeletedSavedValueIds((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
                setTypeChangeChoice('wipe');
                setPendingTypeChange(null);
                handleSaveAll('wipe');
              }}
            >
              Delete values
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setTypeChangeChoice('keep');
                setPendingTypeChange(null);
                handleSaveAll('keep');
              }}
            >
              Keep values
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={showUnsavedConfirm}
        onClose={() => {
          pendingLeaveRef.current = 'close';
          setShowUnsavedConfirm(false);
        }}
        onConfirm={handleDiscardAndClose}
        title="Unsaved Changes"
        message="You have unsaved changes (data values, thresholds, reminders, or other card settings). Are you sure you want to discard them and close?"
        confirmText="Discard"
        cancelText="Keep Editing"
        variant="warning"
      />

      {/* Import Data Modal - for data and status cards */}
      {hasDataValues && (
        <ImportDataModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          nodeId={nodeId}
          nodeName={node?.title || 'Card'}
          periodYear={new Date().getFullYear()}
          periodMonth={new Date().getMonth() + 1}
          isBulk={false}
          onImportPreview={handleImportPreview}
          onImportComplete={() => {
            setPendingDataValues([]);
            setDeletedSavedValueIds([]);
            handleClose();
          }}
        />
      )}

      <ShareModal
        open={mentionShareFlow.showShareModal}
        onClose={mentionShareFlow.handleShareModalClose}
        onApplySuccess={mentionShareFlow.handleShareComplete}
        onAddPeople={async (emails, permission, message, groupIds) => {
          await shareNodeMutation.mutateAsync({
            nodeId: scopeId,
            data: {
              emails: emails.length > 0 ? emails : undefined,
              permission,
              message,
              group_ids: groupIds
            }
          });
        }}
        initialEmails={mentionShareFlow.shareModalEmails}
        title={node?.title || 'Dashboard'}
        shareType="dashboard"
        defaultPermission={ResourcePermissionRole.DATA_ENTRY}
        user_email={user?.email}
        mentionPrompt={
          mentionShareFlow.mentionsNeedingAccess.length > 0
            ? {
                mentionsNeedingAccess: mentionShareFlow.mentionsNeedingAccess,
                recommendedPermissionHint: 'Data entry access is required to add notes.'
              }
            : undefined
        }
      />
    </>
  );
}
